// src/components/Chat/ChatInterface.tsx
import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import { Order, Message, MessageReaction, OrderStatus } from '../../types';
import OrderCard from './OrderCard';
import DepositPage from './DepositPage';
import AdminDepositPage from './AdminDepositPage';
import AdminOrderPricingPage from './AdminOrderPricingPage';
import AdminUserStatisticsPage from './AdminUserStatisticsPage';
import AdminMaintenancePage from './AdminMaintenancePage';
import AdminCloneAccountsPage from './AdminCloneAccountsPage';
import SkinCollabEventsPage from './SkinCollabEventsPage';
import AdminCouponsPage from './AdminCouponsPage';
import ArticlesPage from './ArticlesPage';
import OrderManagementPage from './OrderManagementPage';
import { estimateBulkOrderPrice, getActiveDiscountTierTitle, getOrderInputLimitError, getPricingBonusLabel, OrderBonusTier, OrderDiscountTier, OrderPricingOption, parseBulkOrderInput } from './orderPricing';
import { apiService } from '../../services/api.service';

interface ChatInterfaceProps {
    userId: string;
    username: string;
}

interface DepositHistoryItem {
    transactionId: string;
    amount: number;
    status: string;
    createdAt?: string;
}

interface Conversation {
    roomId: string;
    participantId?: string;
    participantName?: string;
    participantUsername?: string;
    participantRole?: string;
    lastMessage?: string;
    lastMessageTime?: string;
    lastMessageId?: string;
    lastSenderUsername?: string;
    unreadCount?: number;
    hasUnread?: boolean;
}

interface ConversationUserResult {
    _id: string;
    username: string;
    fullName?: string;
    role: string;
}

type ChatNavigation =
    | 'home'
    | 'orders'
    | 'articles'
    | 'recharge'
    | 'admin-recharge'
    | 'admin-pricing'
    | 'admin-coupons'
    | 'admin-statistics'
    | 'admin-maintenance'
    | 'admin-clone-accounts'
    | 'skin-collab-events';

const ORDER_SIDEBAR_PAGE_SIZE = 20;
const CHAT_HISTORY_PAGE_SIZE = 10;
const CHAT_NAVIGATION_ITEMS: ChatNavigation[] = [
    'home', 'orders', 'articles', 'recharge', 'admin-recharge', 'admin-pricing',
    'admin-coupons', 'admin-statistics', 'admin-maintenance', 'admin-clone-accounts',
    'skin-collab-events',
];
const MESSAGE_REACTIONS = [
    { emoji: '❤️', label: 'Tim' },
    { emoji: '😂', label: 'Haha' },
    { emoji: '😢', label: 'Buồn' },
    { emoji: '😮', label: 'Wow' },
    { emoji: '😡', label: 'Phẫn nộ' },
    { emoji: '👍', label: 'Like' },
];

const ChatInterface: React.FC<ChatInterfaceProps> = ({ userId, username }) => {
    const { socketService, isConnected } = useSocket();
    const { user, logout } = useAuth();
    const currentUserId = user?._id || userId || (() => {
        try {
            const savedUser = localStorage.getItem('user_data');
            return savedUser && savedUser !== 'undefined' && savedUser !== 'null' ? JSON.parse(savedUser)._id : '';
        } catch {
            return '';
        }
    })();
    const [messages, setMessages] = useState<Message[]>([]);
    const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
    const [showScrollToLatest, setShowScrollToLatest] = useState(false);
    const [inputMessage, setInputMessage] = useState('');
    const [replyingTo, setReplyingTo] = useState<Message | null>(null);
    const [markingUnreadId, setMarkingUnreadId] = useState<string | null>(null);
    const [messageActionError, setMessageActionError] = useState('');
    const [activeReactionMessageId, setActiveReactionMessageId] = useState<string | null>(null);
    const [reactingMessageId, setReactingMessageId] = useState<string | null>(null);
    const [approvingRedoMessageId, setApprovingRedoMessageId] = useState<string | null>(null);
    const [approvedRedoMessageIds, setApprovedRedoMessageIds] = useState<string[]>([]);
    const [approvingRefundMessageId, setApprovingRefundMessageId] = useState<string | null>(null);
    const [approvedRefundMessageIds, setApprovedRefundMessageIds] = useState<string[]>([]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [isOrderCodeCopied, setIsOrderCodeCopied] = useState(false);
    const [isLoadingOrderPage, setIsLoadingOrderPage] = useState(false);
    const [hasMoreOrders, setHasMoreOrders] = useState(true);
    const [ordersLoadError, setOrdersLoadError] = useState('');
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [unfinishedOrderCount, setUnfinishedOrderCount] = useState<number | null>(null);
    const [successfulOrderCount, setSuccessfulOrderCount] = useState<number | null>(null);
    const [skinExplosionSummary, setSkinExplosionSummary] = useState<{ totalAccountsRun: number; skinExplosionAccounts: number; runningOrders: number } | null>(null);
    const [onlineUserCount, setOnlineUserCount] = useState<number | null>(null);
    const [cloneAccountPendingCount, setCloneAccountPendingCount] = useState<number | null>(null);
    const [cloneAccountPendingCountError, setCloneAccountPendingCountError] = useState('');
    const [unfinishedOrderCountError, setUnfinishedOrderCountError] = useState('');
    const [orderCountRefreshToken, setOrderCountRefreshToken] = useState(0);
    const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null);
    const [updatingPriorityOrderId, setUpdatingPriorityOrderId] = useState<string | null>(null);
    const userRoomId = `user_${String(currentUserId || user?._id || '')}`;
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [isConversationSearchOpen, setIsConversationSearchOpen] = useState(false);
    const [conversationSearch, setConversationSearch] = useState('');
    const [conversationSearchResults, setConversationSearchResults] = useState<ConversationUserResult[]>([]);
    const [isSearchingConversations, setIsSearchingConversations] = useState(false);
    const [conversationSearchError, setConversationSearchError] = useState('');
    const [isLoadingMoreConversations, setIsLoadingMoreConversations] = useState(false);
    const [hasMoreConversations, setHasMoreConversations] = useState(true);
    const [activeRoomId, setActiveRoomId] = useState<string>('');
    const [activeNav, setActiveNav] = useState<ChatNavigation>(() => {
        try {
            const savedNav = localStorage.getItem(`chat-active-nav:${userId}`);
            return savedNav && CHAT_NAVIGATION_ITEMS.includes(savedNav as ChatNavigation)
                ? savedNav as ChatNavigation
                : 'orders';
        } catch {
            return 'orders';
        }
    });
    const [maintenanceEnabled, setMaintenanceEnabled] = useState<boolean | null>(null);
    const [maintenanceStatusError, setMaintenanceStatusError] = useState('');
    const [skinCollabEventsEnabled, setSkinCollabEventsEnabled] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [passwordError, setPasswordError] = useState('');
    const [passwordMessage, setPasswordMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [rechargeHistory, setRechargeHistory] = useState<DepositHistoryItem[]>([]);
    const [isRechargeHistoryLoading, setIsRechargeHistoryLoading] = useState(false);
    const [orderCode, setOrderCode] = useState('');
    const [orderNote, setOrderNote] = useState('');
    const [couponCode, setCouponCode] = useState('');
    const [orderSteps, setOrderSteps] = useState(1);
    const [showBulkOrderHelp, setShowBulkOrderHelp] = useState(false);
    const [orderEntryError, setOrderEntryError] = useState('');
    const [orderEntryMessage, setOrderEntryMessage] = useState('');
    const [orderPricing, setOrderPricing] = useState<OrderPricingOption[]>([]);
    const [orderBonusTiers, setOrderBonusTiers] = useState<OrderBonusTier[]>([]);
    const [orderDiscountTitle, setOrderDiscountTitle] = useState<string>();
    const hasInitializedOrderStepsRef = useRef(false);
    const selectedOrderPricing = orderPricing.find((item) => item.totalSteps === orderSteps);
    const orderPriceEstimate = estimateBulkOrderPrice(orderCode, orderSteps, orderPricing);
    const [partnerOutstanding, setPartnerOutstanding] = useState({ unpaidTotal: 0, outstandingSteps: 0 });
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messageListRef = useRef<HTMLDivElement>(null);
    const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const historyCursorRef = useRef<string | undefined>(undefined);
    const hasMoreHistoryRef = useRef(false);
    const isLoadingOlderMessagesRef = useRef(false);
    const pendingHistoryBeforeRef = useRef<string | null>(null);
    const pendingScrollRestoreRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
    const conversationCursorRef = useRef<{ lastMessageTime: string; roomId: string } | undefined>(undefined);
    const hasMoreConversationsRef = useRef(true);
    const hasLoadedOlderConversationsRef = useRef(false);
    const isLoadingMoreConversationsRef = useRef(false);
    const pendingConversationBeforeRef = useRef<{ lastMessageTime: string; roomId: string } | null>(null);
    const conversationListRef = useRef<HTMLDivElement>(null);
    const scrollToLatestRef = useRef(false);
    const orderListRef = useRef<HTMLDivElement>(null);
    const orderOffsetRef = useRef(0);
    const orderPageLoadingRef = useRef(false);
    const hasMoreOrdersRef = useRef(true);
    const retryOrderPageFromStartRef = useRef(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const accountMenuRef = useRef<HTMLDivElement>(null);
    const navigationRef = useRef<HTMLElement>(null);

    useEffect(() => {
        try {
            localStorage.setItem(`chat-active-nav:${userId}`, activeNav);
        } catch (error) {
            console.warn('Could not save the active chat tab:', error);
        }
    }, [activeNav, userId]);

    useEffect(() => {
        const navigation = navigationRef.current;
        const activeButton = navigation?.querySelector<HTMLButtonElement>('[aria-current="page"]');
        if (!navigation || !activeButton || navigation.scrollWidth <= navigation.clientWidth) return;
        navigation.scrollTo({
            left: Math.max(0, activeButton.offsetLeft - (navigation.clientWidth - activeButton.offsetWidth) / 2),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
    }, [activeNav, user?.role]);
    const bulkOrderHelpButtonRef = useRef<HTMLButtonElement>(null);
    const bulkOrderHelpPopupRef = useRef<HTMLDivElement>(null);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const partnerSummaryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const displayName = user?.fullName || user?.username || 'Người dùng';
    const refreshMaintenanceStatus = useCallback(async () => {
        try {
            const status = await apiService.get<{ enabled: boolean }>('/system-settings/maintenance');
            setMaintenanceEnabled(status.enabled);
            setMaintenanceStatusError('');
        } catch (error) {
            console.error('Không tải được trạng thái bảo trì:', error);
            setMaintenanceEnabled(null);
            setMaintenanceStatusError('Không tải được trạng thái bảo trì. Tạm thời không thể tạo đơn hàng hoặc nạp tiền.');
        }
    }, []);

    useEffect(() => {
        void refreshMaintenanceStatus();
    }, [refreshMaintenanceStatus]);

    const refreshSkinCollabEventsStatus = useCallback(async () => {
        try {
            const status = await apiService.get<{ enabled: boolean }>('/system-settings/skin-collab-events');
            setSkinCollabEventsEnabled(status.enabled);
        } catch (error) {
            console.error('Could not load skin collaboration event visibility:', error);
            setSkinCollabEventsEnabled(false);
        }
    }, []);

    useEffect(() => {
        void refreshSkinCollabEventsStatus();
    }, [refreshSkinCollabEventsStatus]);

    const refreshPartnerOutstanding = useCallback(() => {
        if (user?.role !== 'partner') return;
        if (partnerSummaryTimerRef.current) clearTimeout(partnerSummaryTimerRef.current);
        partnerSummaryTimerRef.current = setTimeout(() => {
            void apiService.get<{ unpaidTotal: number; outstandingSteps: number }>('/orders/summary')
                .then(setPartnerOutstanding)
                .catch((error) => console.error('Không tải được tổng công nợ đơn hàng:', error));
        }, 300);
    }, [user?.role]);

    const loadOrderPage = useCallback(async (reset = false) => {
        if (orderPageLoadingRef.current || (!reset && !hasMoreOrdersRef.current)) return;

        orderPageLoadingRef.current = true;
        setIsLoadingOrderPage(true);
        setOrdersLoadError('');
        const offset = reset ? 0 : orderOffsetRef.current;
        retryOrderPageFromStartRef.current = reset;

        try {
            const response = await apiService.get<{ total: number; data: Order[] }>(
                `/orders?limit=${ORDER_SIDEBAR_PAGE_SIZE}&offset=${offset}`,
            );
            const receivedOrders = response.data || [];
            orderOffsetRef.current = offset + receivedOrders.length;
            const hasMore = orderOffsetRef.current < response.total;
            hasMoreOrdersRef.current = hasMore;
            setHasMoreOrders(hasMore);
            setOrders((currentOrders) => {
                if (reset) return receivedOrders;
                const existingIds = new Set(currentOrders.map((order) => order._id));
                return [...currentOrders, ...receivedOrders.filter((order) => !existingIds.has(order._id))];
            });
        } catch (error) {
            console.error('Không tải được danh sách đơn hàng:', error);
            setOrdersLoadError('Không tải được danh sách đơn hàng.');
        } finally {
            orderPageLoadingRef.current = false;
            setIsLoadingOrderPage(false);
        }
    }, []);

    useEffect(() => {
        if (activeNav === 'home' && user?.role !== 'admin') {
            void loadOrderPage(true);
        }
    }, [activeNav, user?.role, loadOrderPage]);

    useEffect(() => {
        if (activeNav !== 'orders') return;

        let isCurrent = true;
        apiService.get<{ total: number; successfulTotal: number }>('/orders/unfinished-count')
            .then((response) => {
                if (!isCurrent) return;
                setUnfinishedOrderCount(response.total);
                setSuccessfulOrderCount(response.successfulTotal);
                setUnfinishedOrderCountError('');
            })
            .catch((error) => {
                if (!isCurrent) return;
                console.error('Không tải được tổng số đơn hàng chưa hoàn thành:', error);
                setUnfinishedOrderCountError('Không tải được tổng số đơn hàng chưa hoàn thành.');
            });

        return () => {
            isCurrent = false;
        };
    }, [activeNav, orderCountRefreshToken]);

    useEffect(() => {
        if (activeNav !== 'orders' && activeNav !== 'skin-collab-events') return;
        let isCurrent = true;
        apiService.get<{ totalAccountsRun: number; skinExplosionAccounts: number; runningOrders: number }>('/skin-collab-events/system-outcome-summary')
            .then((response) => {
                if (isCurrent) setSkinExplosionSummary(response);
            })
            .catch((error) => console.error('Không tải được thống kê acc nổ skin:', error));
        return () => { isCurrent = false; };
    }, [activeNav]);

    useEffect(() => {
        if (activeNav !== 'orders' || user?.role !== 'admin') return;

        let isCurrent = true;
        const loadCloneAccountPendingCount = async () => {
            try {
                const response = await apiService.get<{ pending: number }>('/orders/clone-accounts-pending-count');
                if (!isCurrent) return;
                setCloneAccountPendingCount(response.pending);
                setCloneAccountPendingCountError('');
            } catch (error) {
                if (!isCurrent) return;
                console.error('Không tải được số acc clone còn lại:', error);
                setCloneAccountPendingCountError('Không tải được số acc clone còn lại.');
            }
        };

        void loadCloneAccountPendingCount();
        const refreshTimer = window.setInterval(() => {
            void loadCloneAccountPendingCount();
        }, 60_000);

        return () => {
            isCurrent = false;
            window.clearInterval(refreshTimer);
        };
    }, [activeNav, user?.role]);

    useEffect(() => {
        const handleOnlineUserCount = (data: { count: number }) => {
            if (Number.isInteger(data?.count) && data.count >= 0) {
                setOnlineUserCount(data.count);
            }
        };

        socketService.on('visitors:count', handleOnlineUserCount);
        if (isConnected) {
            socketService.emit('visitors:count:get');
        }

        return () => socketService.off('visitors:count', handleOnlineUserCount);
    }, [isConnected, socketService]);

    const handleOrderListScroll = () => {
        const list = orderListRef.current;
        if (list && list.scrollHeight - list.scrollTop - list.clientHeight < 120) {
            void loadOrderPage();
        }
    };

    useEffect(() => {
        refreshPartnerOutstanding();
        return () => {
            if (partnerSummaryTimerRef.current) clearTimeout(partnerSummaryTimerRef.current);
        };
    }, [orders, refreshPartnerOutstanding]);

    const initials = (displayName || 'N')
        .split(' ')
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    const loadRechargeHistory = async () => {
        setIsRechargeHistoryLoading(true);
        try {
            const response = await apiService.get<{ data: DepositHistoryItem[] }>('/transactions/history?limit=3&offset=0');
            setRechargeHistory(response.data || []);
        } finally {
            setIsRechargeHistoryLoading(false);
        }
    };

    useEffect(() => {
        Promise.all([
            apiService.get<OrderPricingOption[]>('/orders/pricing'),
            apiService.get<OrderBonusTier[]>('/orders/bonus-tiers'),
        ])
            .then(([pricing, bonusTiers]) => {
                setOrderBonusTiers(bonusTiers);
                const availablePricing = pricing || [];
                setOrderPricing(availablePricing);
                if (availablePricing.length === 0) return;

                setOrderSteps((currentSteps) => {
                    const currentSelectionExists = availablePricing.some(
                        (item) => item.totalSteps === currentSteps,
                    );
                    if (hasInitializedOrderStepsRef.current && currentSelectionExists) {
                        return currentSteps;
                    }

                    hasInitializedOrderStepsRef.current = true;
                    return availablePricing[0].totalSteps;
                });
            })
            .catch(() => { setOrderPricing([]); setOrderBonusTiers([]); });
    }, [activeNav, user?._id, user?.role]);

    useEffect(() => {
        if (!user?._id || (user.role !== 'user' && user.role !== 'partner')) {
            setOrderDiscountTitle(undefined);
            return;
        }

        let isCurrent = true;
        Promise.all([
            apiService.get<{ totalDeposited?: number }>('/transactions/wallet'),
            apiService.get<OrderDiscountTier[]>('/transactions/discount-tiers'),
        ])
            .then(([wallet, tiers]) => {
                if (isCurrent) {
                    setOrderDiscountTitle(getActiveDiscountTierTitle(wallet.totalDeposited || 0, tiers || []));
                }
            })
            .catch((error) => {
                console.error('Không tải được tên hạng khuyến mãi:', error);
            });

        return () => {
            isCurrent = false;
        };
    }, [user?._id, user?.role]);

    useEffect(() => {
        if (!showBulkOrderHelp) return;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (bulkOrderHelpButtonRef.current?.contains(target) || bulkOrderHelpPopupRef.current?.contains(target)) return;
            setShowBulkOrderHelp(false);
        };

        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [showBulkOrderHelp]);

    const formatRechargeDate = (value?: string) => value
        ? new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value))
        : '-';

    const getRechargeStatusLabel = (status: string) => ({
        success: 'Thành công',
        pending: 'Đang xử lý',
        pending_admin: 'Chờ admin duyệt',
        processing: 'Đang xử lý',
        expired: 'Hết hạn',
        failed: 'Thất bại',
        cancelled: 'Đã hủy',
    }[status] || status);

    const getRoleLabel = (role?: string) => ({
        admin: 'Quản trị viên',
        partner: 'Đối tác',
        user: 'Thành viên',
    }[role || ''] || role || 'Thành viên');

    const getOrderStatusLabel = (status?: string) => ({
        PENDING: 'Đang chờ',
        CODE_RECEIVED: 'Đã nhận mã',
        PROCESSING: 'Đang xử lý',
        COMPLETED: 'Hoàn thành',
        ERROR: 'Lỗi',
        CANCELED: 'Đã hủy',
    }[status || ''] || status || 'Không xác định');

    const formatMessageTime = (timestamp: Date) =>
        new Intl.DateTimeFormat('vi-VN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).format(new Date(timestamp));

    const isSameCalendarDay = (first: Date, second: Date) => {
        const firstDate = new Date(first);
        const secondDate = new Date(second);

        return firstDate.getFullYear() === secondDate.getFullYear()
            && firstDate.getMonth() === secondDate.getMonth()
            && firstDate.getDate() === secondDate.getDate();
    };

    const formatMessageDay = (timestamp: Date) => {
        const messageDate = new Date(timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        if (isSameCalendarDay(messageDate, today)) return 'Hôm nay';
        if (isSameCalendarDay(messageDate, yesterday)) return 'Hôm qua';

        return new Intl.DateTimeFormat('vi-VN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(messageDate);
    };

    // Join chat room and listen to messages
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        console.log('💬 ChatInterface - isConnected:', isConnected);

        if (!isConnected || !user) {
            console.log('⏳ Waiting for socket connection and user...');
            return;
        }

        const roomId = user.role === 'admin' ? (activeRoomId || currentUserId) : userRoomId;
        historyCursorRef.current = undefined;
        hasMoreHistoryRef.current = false;
        isLoadingOlderMessagesRef.current = false;
        pendingHistoryBeforeRef.current = null;
        pendingScrollRestoreRef.current = null;
        scrollToLatestRef.current = false;
        setIsLoadingOlderMessages(false);
        setMessages([]);
        if (user.role === 'admin') {
            socketService.getConversations(currentUserId, true);
        }

        if (roomId) {
            socketService.joinRoom(roomId);
            socketService.getHistory(roomId, CHAT_HISTORY_PAGE_SIZE);
            console.log('🔗 Joined room:', roomId, 'userId:', currentUserId);
        }

        const handleHistory = (data: any) => {
            if (data?.roomId && String(data.roomId) !== String(roomId)) return;
            const history = Array.isArray(data) ? data : data?.messages || data?.data || [];
            const normalized = history.map((item: any) => ({
                id: item._id || item.id || `msg-${Date.now()}-${Math.random()}`,
                _id: item._id, // Store MongoDB ID for updates
                userId: item.userId || item.senderId || item.authorId || currentUserId,
                username: item.username || item.senderUsername || item.authorName || 'Người dùng',
                content: item.content || '',
                type: item.type || 'text',
                orderId: item.orderId,
                replyTo: item.replyTo,
                progress: item.progress,
                currentStep: item.currentStep,
                totalSteps: item.totalSteps,
                timestamp: new Date(item.createdAt || item.timestamp || Date.now()),
                isAdmin: Boolean(item.isAdmin || item.role === 'admin'),
                status: item.status || 'sent',
                isRead: Boolean(item.isRead),
                reactions: item.reactions || [],
            }));
            const requestedBefore = data?.before || null;
            if (requestedBefore && requestedBefore !== pendingHistoryBeforeRef.current) return;

            if (requestedBefore) {
                pendingScrollRestoreRef.current = {
                    scrollHeight: messageListRef.current?.scrollHeight || 0,
                    scrollTop: messageListRef.current?.scrollTop || 0,
                };
                setMessages((currentMessages) => sortSelectedOrderMessages(
                    dedupeMessages([...normalized, ...currentMessages]),
                    selectedOrder?._id,
                ));
                pendingHistoryBeforeRef.current = null;
                isLoadingOlderMessagesRef.current = false;
                setIsLoadingOlderMessages(false);
            } else {
                scrollToLatestRef.current = true;
                setMessages(sortSelectedOrderMessages(dedupeMessages(normalized), selectedOrder?._id));
            }

            historyCursorRef.current = normalized.length
                ? new Date(normalized[0].timestamp).toISOString()
                : historyCursorRef.current;
            hasMoreHistoryRef.current = Boolean(data?.hasMore);
        };

        const handleConversations = (data: any) => {
            const list = Array.isArray(data) ? data : data?.conversations || data?.data || [];
            const mapped = list.map((item: any) => {
                const roomIdValue = item.roomId || item._id || item.userId || '';
                const participantName = item.displayName || item.participantFullName || item.participantUsername || item.userName || item.username || item.user?.username || item.participantName || item.participant?.username || item.roomName || '';
                const lastSenderUsername = item.lastSenderUsername || item.senderUsername || '';
                const participantId = item.participantId || (String(roomIdValue).startsWith('user_') ? String(roomIdValue).slice(5) : roomIdValue);

                const sanitizeName = (name?: string) => {
                    if (!name) return '';
                    const trimmed = String(name).trim();
                    if (!trimmed) return '';
                    const normalized = trimmed.toLowerCase();
                    if (normalized === 'admin' || normalized === 'system' || normalized === user?.username?.toLowerCase()) {
                        return '';
                    }
                    return trimmed;
                };

                const chosenName = [participantName, lastSenderUsername]
                    .map((name) => sanitizeName(name))
                    .find(Boolean) || roomIdValue;

                const unreadCount = Number(item.unreadCount || item.unread || 0);
                const hasUnread = Boolean(item.hasUnread ?? unreadCount > 0);

                return {
                    roomId: roomIdValue,
                    participantId,
                    participantName: chosenName,
                    participantUsername: item.participantUsername || item.username || '',
                    participantRole: item.participantRole || item.role || item.user?.role || 'user',
                    lastMessage: item.lastMessage || item.lastText || '',
                    lastMessageTime: item.lastMessageTime,
                    lastSenderUsername: chosenName,
                    unreadCount,
                    hasUnread,
                };
            });

            const normalizedRooms: Conversation[] = mapped.map((room: Conversation) => room);

            const requestedBefore = data?.before || null;
            if (requestedBefore) {
                if (
                    !pendingConversationBeforeRef.current
                    || requestedBefore.lastMessageTime !== pendingConversationBeforeRef.current.lastMessageTime
                    || requestedBefore.roomId !== pendingConversationBeforeRef.current.roomId
                ) return;

                setConversations((current) => [...current, ...normalizedRooms]
                    .reduce((uniqueRooms, room) => {
                        const existingIndex = uniqueRooms.findIndex((item) => item.roomId === room.roomId);
                        if (existingIndex === -1) uniqueRooms.push(room);
                        else uniqueRooms[existingIndex] = room;
                        return uniqueRooms;
                    }, [] as typeof normalizedRooms)
                    .sort((left, right) => new Date(right.lastMessageTime || 0).getTime()
                        - new Date(left.lastMessageTime || 0).getTime()));
                pendingConversationBeforeRef.current = null;
                isLoadingMoreConversationsRef.current = false;
                setIsLoadingMoreConversations(false);
                hasLoadedOlderConversationsRef.current = true;
                conversationCursorRef.current = data?.nextCursor;
                hasMoreConversationsRef.current = Boolean(data?.hasMore);
            } else {
                setConversations((current) => {
                    if (current.length <= normalizedRooms.length) return normalizedRooms;
                    const byRoomId = new Map(current.map((room) => [room.roomId, room]));
                    normalizedRooms.forEach((room: { roomId: string }) => byRoomId.set(room.roomId, room));
                    return Array.from(byRoomId.values())
                        .sort((left, right) => new Date(right.lastMessageTime || 0).getTime()
                            - new Date(left.lastMessageTime || 0).getTime());
                });
                if (!activeRoomId && list.length > 0) {
                    setActiveRoomId(list[0].roomId || currentUserId);
                }
                if (!hasLoadedOlderConversationsRef.current) {
                    conversationCursorRef.current = data?.nextCursor;
                    hasMoreConversationsRef.current = Boolean(data?.hasMore);
                }
            }

            setHasMoreConversations(hasMoreConversationsRef.current);
        };

        const handleMessage = (message: any) => {
            console.log('💬 New message:', message);

            const incomingUserId = message.userId || message.senderId || message.authorId || message._userId;
            const incomingUsername = message.username || message.senderUsername || message.authorName || 'Người dùng';
            const nextMessage: Message = {
                id: message._id || message.id || `msg-${Date.now()}-${Math.random()}`,
                _id: message._id, // Store MongoDB ID for updates
                userId: incomingUserId || currentUserId,
                username: incomingUsername,
                content: message.content || '',
                type: message.type || 'text',
                orderId: message.orderId,
                replyTo: message.replyTo,
                progress: message.progress,
                currentStep: message.currentStep,
                totalSteps: message.totalSteps,
                timestamp: new Date(message.createdAt || message.timestamp || Date.now()),
                isAdmin: Boolean(message.isAdmin || false),
                status: message.status || 'sent',
                isRead: Boolean(message.isRead),
                reactions: message.reactions || [],
            };

            const messageRoomId = message.roomId || message.room || message.conversationId || roomId;
            const roomMatch = messageRoomId ? String(messageRoomId) === String(roomId) : true;

            if (user.role === 'admin' && messageRoomId) {
                const conversationRoomId = String(messageRoomId);
                const participantId = conversationRoomId.startsWith('user_')
                    ? conversationRoomId.slice(5)
                    : conversationRoomId;
                setConversations((current) => {
                    const existing = current.find((item) => item.roomId === conversationRoomId);
                    if (existing?.lastMessageId === nextMessage.id) return current;
                    const updated = {
                        roomId: conversationRoomId,
                        participantId,
                        participantName: existing?.participantName || (nextMessage.isAdmin ? 'Người dùng' : incomingUsername),
                        participantUsername: existing?.participantUsername || (nextMessage.isAdmin ? '' : incomingUsername),
                        participantRole: existing?.participantRole || 'user',
                        lastMessage: nextMessage.content,
                        lastMessageTime: nextMessage.timestamp.toISOString(),
                        lastMessageId: nextMessage.id,
                        lastSenderUsername: incomingUsername,
                        unreadCount: (existing?.unreadCount || 0)
                            + (!nextMessage.isAdmin && conversationRoomId !== activeRoomId ? 1 : 0),
                    };
                    return [updated, ...current.filter((item) => item.roomId !== conversationRoomId)]
                        .sort((left, right) => new Date(right.lastMessageTime || 0).getTime()
                            - new Date(left.lastMessageTime || 0).getTime());
                });
            }

            if (roomMatch) {
                const list = messageListRef.current;
                if (!list || list.scrollHeight - list.scrollTop - list.clientHeight < 100) {
                    scrollToLatestRef.current = true;
                }
                setMessages(prev => sortSelectedOrderMessages(dedupeMessages([...prev, nextMessage]), selectedOrder?._id));
            }
        };

        const handleMessageMarkedUnread = (data: { messageId: string; roomId: string }) => {
            if (data.roomId === roomId) {
                setMessages((currentMessages) => currentMessages.map((message) =>
                    message._id === data.messageId
                        ? { ...message, isRead: false, status: 'delivered' }
                        : message,
                ));
            }
            if (user.role === 'admin') socketService.getConversations(currentUserId, true);
        };

        const handleMessageReactions = (data: { messageId: string; roomId: string; reactions: MessageReaction[] }) => {
            if (data.roomId !== roomId) return;
            setMessages((currentMessages) => currentMessages.map((message) =>
                message._id === data.messageId
                    ? { ...message, reactions: data.reactions }
                    : message,
            ));
        };

        socketService.on('chat:history', handleHistory);
        socketService.on('conversations:list', handleConversations);
        socketService.on('chat:message', handleMessage);
        socketService.on('chat:unread', handleMessageMarkedUnread);
        socketService.on('chat:message:reactions', handleMessageReactions);

        const isVisibleToCurrentUser = (order: Pick<Order, 'userId'>) =>
            user.role === 'admin' || String(order.userId) === String(currentUserId);

        const handleOrderCreated = (order: Order) => {
            console.log('Order created:', order);
            setOrderCountRefreshToken((current) => current + 1);
            if (!isVisibleToCurrentUser(order)) return;
            setOrders(prev => [order, ...prev.filter((existing) => existing._id !== order._id)]);
        };

        const handleOrderUpdated = (order: Order) => {
            console.log('Order updated:', order);
            setOrderCountRefreshToken((current) => current + 1);
            if (!isVisibleToCurrentUser(order)) {
                setOrders(prev => prev.filter(o => o._id !== order._id));
                if (selectedOrder?._id === order._id) setSelectedOrder(null);
                return;
            }
            setOrders(prev => prev.map(o => o._id === order._id ? order : o));
            setSelectedOrder(current => current?._id === order._id ? { ...current, ...order } : current);
        };

        const handleOrderStatus = (data: any) => {
            console.log('Order status received:', data);
            setOrderCountRefreshToken((current) => current + 1);
            // Update selectedOrder if it's the same order
            if (selectedOrder && data.orderId && String(selectedOrder._id) === String(data.orderId)) {
                const updatedOrder: Order = {
                    _id: data.orderId,
                    code: data.code,
                    userId: data.userId || selectedOrder?.userId || '',
                    username: data.username || selectedOrder?.username || 'Không xác định',
                    status: data.status,
                    progress: data.progress || 0,
                    currentStep: data.currentStep || 0,
                    totalSteps: data.totalSteps || 8,
                    processedSteps: data.processedSteps ?? selectedOrder.processedSteps,
                    successfulSteps: data.successfulSteps ?? selectedOrder.successfulSteps,
                    failedSteps: data.failedSteps ?? selectedOrder.failedSteps,
                    shopItemsDone: data.shopItemsDone ?? selectedOrder.shopItemsDone,
                    message: data.message ?? selectedOrder.message,
                    error: data.error ?? selectedOrder.error,
                    createdAt: selectedOrder?.createdAt || new Date(),
                    updatedAt: new Date(),
                };
                setSelectedOrder(updatedOrder);
                setOrders(prev => prev.map(o => String(o._id) === String(data.orderId) ? { ...o, ...updatedOrder } : o));
                
                // Also update the progress message in messages array
                setMessages(prev => prev.map(msg => {
                    if (msg.type === 'order' && msg.orderId === data.orderId) {
                        return {
                            ...msg,
                            progress: data.progress || 0,
                            currentStep: data.currentStep || 0,
                            totalSteps: data.totalSteps || 8,
                        };
                    }
                    return msg;
                }));
            }
        };

        const handleChatOrderStatus = (data: any) => {
            setMessages((currentMessages) => currentMessages.map((message) =>
                message.type === 'order' && message.orderId === data.orderId
                    ? {
                        ...message,
                        orderStatus: data.status,
                        progress: data.progress,
                        currentStep: data.currentStep,
                        totalSteps: data.totalSteps,
                    }
                    : message,
            ));
        };

        const handleOrderStepDone = (data: any) => {
            console.log('Step done:', data);
            addMessage({
                id: `msg-${Date.now()}`,
                userId: 'system',
                username: 'System',
                content: `✅ ${data.message}`,
                type: 'text',
                timestamp: new Date(),
                isAdmin: true,
            });
        };

        const handleOrderError = (error: any) => {
            console.log('Order error:', error);
            addMessage({
                id: `msg-${Date.now()}`,
                userId: 'system',
                username: 'System',
                content: `❌ ${error.message || 'Đã xảy ra lỗi với đơn hàng'}`,
                type: 'text',
                timestamp: new Date(),
                isAdmin: true,
            });
        };

        const handleMessageUpdated = (updatedMessage: any) => {
            console.log('Message updated (order progress):', updatedMessage);
            // Update the message in the list with new progress
            setMessages(prev => {
                const index = prev.findIndex(m => {
                    // Match by MongoDB _id first (most reliable)
                    if (updatedMessage._id && m._id === updatedMessage._id) return true;
                    // Fallback to id comparison
                    if (m.id === updatedMessage._id || m.id === updatedMessage.id) return true;
                    // Last resort: match by type and orderId for order messages
                    if (m.type === 'order' && updatedMessage.orderId && m.orderId === updatedMessage.orderId) {
                        return true;
                    }
                    return false;
                });
                
                if (index !== -1) {
                    const updated = [...prev];
                    updated[index] = {
                        ...updated[index],
                        content: updatedMessage.content,
                        progress: updatedMessage.progress,
                        currentStep: updatedMessage.currentStep,
                        totalSteps: updatedMessage.totalSteps,
                    };
                    console.log('✅ Message updated at index:', index, updated[index]);
                    return sortSelectedOrderMessages(dedupeMessages(updated), selectedOrder?._id);
                } else {
                    console.warn('⚠️ Message not found for update:', updatedMessage._id, 'Current messages:', prev.map(m => ({ id: m.id, _id: m._id, type: m.type })));
                }
                return prev;
            });
        };

        socketService.on('order:created', handleOrderCreated);
        socketService.on('order:recreated', handleOrderCreated);
        socketService.on('order:updated', handleOrderUpdated);
        socketService.on('order:status', handleOrderStatus);
        socketService.on('chat:order:status', handleChatOrderStatus);
        socketService.on('order:step:done', handleOrderStepDone);
        socketService.on('order:error', handleOrderError);
        socketService.on('chat:message:updated', handleMessageUpdated);

        return () => {
            socketService.off('chat:message', handleMessage);
            socketService.off('chat:history', handleHistory);
            socketService.off('conversations:list', handleConversations);
            socketService.off('chat:unread', handleMessageMarkedUnread);
            socketService.off('chat:message:reactions', handleMessageReactions);
            socketService.off('order:created', handleOrderCreated);
            socketService.off('order:recreated', handleOrderCreated);
            socketService.off('order:updated', handleOrderUpdated);
            socketService.off('order:status', handleOrderStatus);
            socketService.off('chat:order:status', handleChatOrderStatus);
            socketService.off('order:step:done', handleOrderStepDone);
            socketService.off('order:error', handleOrderError);
            socketService.off('chat:message:updated', handleMessageUpdated);
            if (user.role === 'admin' && roomId) socketService.leaveRoom(roomId);
        };
    }, [isConnected, user, userId, currentUserId, activeRoomId]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
                setIsAccountMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const dedupeMessages = (items: Message[]) => {
        const seen = new Set<string>();

        return items.filter((item) => {
            const idValue = item.id || `${item.userId}-${item.content}-${new Date(item.timestamp).getTime()}`;
            if (seen.has(idValue)) {
                return false;
            }
            seen.add(idValue);
            return true;
        });
    };

    const sortSelectedOrderMessages = (items: Message[], orderId?: string) => {
        if (!orderId) return items;

        const selectedOrderMessages = items.filter(
            (item) => item.type === 'order' && item.orderId === orderId,
        );
        const otherMessages = items.filter(
            (item) => !(item.type === 'order' && item.orderId === orderId),
        );

        const orderedSelected = [...selectedOrderMessages].sort((a, b) => {
            const timeA = new Date(a.timestamp).getTime();
            const timeB = new Date(b.timestamp).getTime();
            return timeB - timeA;
        });

        return [...orderedSelected, ...otherMessages];
    };

    const focusSelectedOrderMessage = (orderId?: string) => {
        if (!orderId) return;

        const targetMessage = [...messages].reverse().find(
            (item) => item.type === 'order' && item.orderId === orderId,
        );

        if (!targetMessage) return;

        const key = targetMessage._id || targetMessage.id;
        const el = messageRefs.current[key];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('ring-2', 'ring-blue-400');
            window.setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400'), 1200);
        }
    };

    useLayoutEffect(() => {
        const list = messageListRef.current;
        if (!list) return;

        const previousPosition = pendingScrollRestoreRef.current;
        if (previousPosition) {
            list.scrollTop = previousPosition.scrollTop + list.scrollHeight - previousPosition.scrollHeight;
            pendingScrollRestoreRef.current = null;
            return;
        }

        if (scrollToLatestRef.current) {
            list.scrollTop = list.scrollHeight;
            scrollToLatestRef.current = false;
            return;
        }

        if (selectedOrder?._id) {
            focusSelectedOrderMessage(selectedOrder._id);
        }
    }, [messages, selectedOrder?._id]);

    const handleMessageListScroll = () => {
        const list = messageListRef.current;
        const before = historyCursorRef.current;
        if (list) {
            setShowScrollToLatest(list.scrollHeight - list.scrollTop - list.clientHeight > 160);
        }
        if (
            !list
            || list.scrollTop > 60
            || !before
            || !hasMoreHistoryRef.current
            || isLoadingOlderMessagesRef.current
        ) {
            return;
        }

        pendingHistoryBeforeRef.current = before;
        pendingScrollRestoreRef.current = {
            scrollHeight: list.scrollHeight,
            scrollTop: list.scrollTop,
        };
        isLoadingOlderMessagesRef.current = true;
        setIsLoadingOlderMessages(true);
        const historyRoomId = user?.role === 'admin'
            ? (activeRoomId || currentUserId)
            : userRoomId;
        socketService.getHistory(historyRoomId, CHAT_HISTORY_PAGE_SIZE, before);
    };

    const scrollToLatestMessage = () => {
        const list = messageListRef.current;
        if (!list) return;
        list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
        setShowScrollToLatest(false);
    };

    const addMessage = (message: Message) => {
        const list = messageListRef.current;
        if (!list || list.scrollHeight - list.scrollTop - list.clientHeight < 100) {
            scrollToLatestRef.current = true;
        }
        setMessages(prev => sortSelectedOrderMessages(dedupeMessages([...prev, message]), selectedOrder?._id));
    };

    const handleLogout = () => {
        logout();
        setIsAccountMenuOpen(false);
    };

    const handleChangePassword = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!user?._id) return;
        setPasswordError('');
        setPasswordMessage('');
        if (passwordForm.newPassword.length < 6) {
            setPasswordError('Mật khẩu mới phải có ít nhất 6 ký tự.');
            return;
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setPasswordError('Mật khẩu xác nhận không khớp.');
            return;
        }

        setIsChangingPassword(true);
        try {
            await apiService.post(`/users/${user._id}/password`, {
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword,
            });
            setPasswordMessage('Đổi mật khẩu thành công.');
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (requestError: any) {
            setPasswordError(requestError.response?.data?.message || 'Không thể đổi mật khẩu. Vui lòng thử lại.');
        } finally {
            setIsChangingPassword(false);
        }
    };

    const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
        const touch = event.touches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
        const start = touchStartRef.current;
        touchStartRef.current = null;
        if (!start) return;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - start.x;
        const deltaY = touch.clientY - start.y;
        if (Math.abs(deltaX) < 60 || Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;

        if (deltaX > 0 && !isSidebarOpen) {
            setIsSidebarOpen(true);
        } else if (deltaX < 0 && isSidebarOpen) {
            setIsSidebarOpen(false);
        }
    };

    const handleSendMessage = () => {
        if (!inputMessage.trim()) return;

        const savedUser = localStorage.getItem('user_data');
        const currentUser = user || (savedUser && savedUser !== 'undefined' && savedUser !== 'null' ? JSON.parse(savedUser) : null);

        if (!currentUser || !currentUser._id) {
            console.warn('⚠️ Không thể gửi tin nhắn: chưa tải được thông tin người dùng');
            return;
        }

        const roomId = currentUser.role === 'admin' ? (activeRoomId || currentUser._id) : `user_${currentUser._id}`;

        console.log('📤 Sending message to room:', roomId, 'user:', currentUser);
        socketService.sendReplyMessage(inputMessage.trim(), roomId, replyingTo?._id);

        setInputMessage('');
        setReplyingTo(null);
        setMessageActionError('');
    };

    const handleSelectRoom = (roomId: string) => {
        setActiveRoomId(roomId);
        setConversations(prev => prev.map((room) =>
            room.roomId === roomId ? { ...room, unreadCount: 0 } : room
        ));
        setMessages([]);
        historyCursorRef.current = undefined;
        hasMoreHistoryRef.current = false;
        pendingHistoryBeforeRef.current = null;
        pendingScrollRestoreRef.current = null;
        scrollToLatestRef.current = false;
        setReplyingTo(null);
        setMessageActionError('');
    };

    useEffect(() => {
        if (!isConversationSearchOpen || user?.role !== 'admin') return;
        const query = conversationSearch.trim();
        if (query.length < 2) {
            setConversationSearchResults([]);
            setConversationSearchError('');
            setIsSearchingConversations(false);
            return;
        }

        setConversationSearchResults([]);
        setConversationSearchError('');
        setIsSearchingConversations(false);
        let cancelled = false;
        const timeout = window.setTimeout(() => {
            setIsSearchingConversations(true);
            setConversationSearchError('');
            apiService.get<ConversationUserResult[]>(
                `/users/chat-search?search=${encodeURIComponent(query)}`,
            ).then((results) => {
                if (!cancelled) setConversationSearchResults(results);
            }).catch((error: any) => {
                if (!cancelled) {
                    setConversationSearchError(
                        error.response?.data?.message || error.message || 'Không tìm được người dùng.',
                    );
                }
            }).finally(() => {
                if (!cancelled) setIsSearchingConversations(false);
            });
        }, 250);

        return () => {
            cancelled = true;
            window.clearTimeout(timeout);
        };
    }, [conversationSearch, isConversationSearchOpen, user?.role]);

    const handleSelectConversationUser = (result: ConversationUserResult) => {
        const roomId = `user_${result._id}`;
        setConversations((current) => [
            {
                roomId,
                participantId: result._id,
                participantName: result.fullName || result.username,
                participantUsername: result.username,
                participantRole: result.role,
                lastMessage: 'Chưa có tin nhắn nào',
                unreadCount: 0,
            },
            ...current.filter((room) => room.roomId !== roomId),
        ]);
        setConversationSearch('');
        setConversationSearchResults([]);
        setIsConversationSearchOpen(false);
        handleSelectRoom(roomId);
    };

    const handleConversationListScroll = () => {
        const list = conversationListRef.current;
        const before = conversationCursorRef.current;
        if (
            !list
            || list.scrollHeight - list.scrollTop - list.clientHeight > 120
            || !before
            || !hasMoreConversationsRef.current
            || isLoadingMoreConversationsRef.current
        ) return;

        pendingConversationBeforeRef.current = before;
        isLoadingMoreConversationsRef.current = true;
        setIsLoadingMoreConversations(true);
        socketService.getConversations(currentUserId, true, before);
    };

    const handleMarkMessageUnread = async (message: Message) => {
        if (!message._id) {
            setMessageActionError('Không thể đánh dấu tin nhắn này vì thiếu mã tin nhắn.');
            return;
        }
        setMarkingUnreadId(message._id);
        setMessageActionError('');
        try {
            await socketService.markMessageUnread(message._id);
            setMessages((currentMessages) => currentMessages.map((item) =>
                item._id === message._id ? { ...item, isRead: false, status: 'delivered' } : item,
            ));
        } catch (error) {
            setMessageActionError(error instanceof Error ? error.message : 'Không thể đánh dấu tin nhắn chưa đọc.');
        } finally {
            setMarkingUnreadId(null);
        }
    };

    const scrollToMessage = (messageId?: string) => {
        if (!messageId) return;
        messageRefs.current[messageId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const isAdmin = user?.role === 'admin';
    const isPartner = user?.role === 'partner';
    const canCancelOrders = user?.role === 'admin' || user?.role === 'partner' || user?.role === 'user';
    const outstandingAmount = partnerOutstanding.unpaidTotal;
    const outstandingSteps = partnerOutstanding.outstandingSteps;
    const selectedConversation = conversations.find((room) => room.roomId === activeRoomId);
    const activeRoomLabel = isAdmin
        ? (selectedConversation?.participantName || selectedConversation?.lastSenderUsername || activeRoomId || 'Phòng trò chuyện')
        : (selectedOrder ? `Đơn hàng ${selectedOrder.code}` : 'Tin nhắn');

    const handleCreateOrder = async () => {
        setOrderEntryError('');
        setOrderEntryMessage('');
        if (maintenanceEnabled !== false) {
            setOrderEntryError(maintenanceEnabled
                ? 'Hệ thống đang bảo trì, tạm thời không thể tạo đơn hàng.'
                : maintenanceStatusError || 'Chưa xác định được trạng thái hệ thống, vui lòng thử lại.');
            return;
        }
        if (!orderCode.trim()) {
            setOrderEntryError('Vui lòng nhập mã đơn hàng.');
            return;
        }

        const ordersToCreate = parseBulkOrderInput(orderCode, orderSteps);

        const invalidLine = ordersToCreate.find((order) => !order.hasValidFormat);
        if (invalidLine || ordersToCreate.length === 0) {
            setOrderEntryError('Mã phải có đúng 10 ký tự chữ hoặc số. Mỗi dòng nhập mã hoặc mã|số lượng (số lượng là số nguyên dương).');
            return;
        }

        const limitError = getOrderInputLimitError(ordersToCreate, orderBonusTiers);
        if (limitError) {
            setOrderEntryError(limitError);
            return;
        }

        const largeStepOrder = ordersToCreate.find((order) => order.totalSteps >= 50);
        if (largeStepOrder) {
            const confirmed = window.confirm(
                `Đơn hàng ${largeStepOrder.code} có ${largeStepOrder.totalSteps} bước. Bạn có chắc chắn muốn tiếp tục tạo đơn này không?`,
            );
            if (!confirmed) {
                return;
            }
        }

        setIsLoading(true);
        try {
            const result = await socketService.createOrders(
                ordersToCreate.map(({ code, totalSteps }) => ({ code, totalSteps })),
                Boolean(largeStepOrder),
                orderNote,
                couponCode.trim() || undefined,
            );
            setOrderCode('');
            setOrderNote('');
            setOrderEntryMessage(`Đã tạo thành công ${result.orders?.length || ordersToCreate.length} đơn hàng.`);
            refreshPartnerOutstanding();
        } catch (requestError: any) {
            setOrderEntryError(requestError.message || 'Không tạo được đơn hàng. Vui lòng thử lại.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleStepOrder = (orderId: string) => {
        socketService.stepOrder(orderId);
    };

    const handleProcessOrder = (orderId: string) => {
        socketService.processOrder(orderId);
    };

    const handleResetOrder = (orderId: string) => {
        socketService.resetOrder(orderId);
    };

    const handleCancelOrder = async (order: Order) => {
        let reason: string | undefined;
        if (isAdmin) {
            const input = window.prompt(`Nhập lý do hủy đơn ${order.code} (tối đa 1000 ký tự):`);
            if (input === null) return;
            reason = input.trim();
            if (!reason || reason.length > 1000) {
                window.alert('Vui lòng nhập lý do hủy đơn từ 1 đến 1000 ký tự.');
                return;
            }
        }
        const feeDescription = (order.unpaidAmount || 0) > 0
            ? 'Phí hủy 5 Like sẽ được giữ lại thành công nợ.'
            : (order.paidAmount || 0) > 0
                ? 'Tiền hoàn về ví sẽ trừ phí hủy 5 Like.'
                : 'Đơn này không phát sinh hoàn tiền hoặc công nợ.';
        if (!isAdmin && !window.confirm(`Hủy đơn ${order.code}? ${feeDescription}`)) return;

        setCancellingOrderId(order._id);
        try {
            const response = await apiService.patch<{ data: Order }>(`/orders/${order._id}/cancel`, { reason });
            setOrders((currentOrders) => currentOrders.map((item) =>
                item._id === order._id ? response.data : item,
            ));
            setSelectedOrder((currentOrder) => currentOrder?._id === order._id ? response.data : currentOrder);
            refreshPartnerOutstanding();
        } catch (error: any) {
            window.alert(error.response?.data?.message || 'Không thể hủy đơn hàng.');
        } finally {
            setCancellingOrderId(null);
        }
    };

    const handleRecreateOrder = (order: Order) => {
        const failedSteps = order.failedSteps || 0;
        if (!failedSteps) return;
        if (window.confirm(`Tạo lại đơn hàng ${order.code} với ${failedSteps} lần lỗi?`)) {
            socketService.recreateOrder(order._id, failedSteps);
        }
    };

    const handleRequestOrderRedo = async (order: Order, reason: string) => {
        const response = await socketService.requestCompletedOrderRedo(order._id, reason);
        if (!response.order) throw new Error('Máy chủ không trả về thông tin yêu cầu làm lại.');
        const updatedOrder = response.order;
        setOrders((currentOrders) => currentOrders.map((item) =>
            item._id === order._id ? updatedOrder : item,
        ));
        setSelectedOrder((currentOrder) => currentOrder?._id === order._id ? updatedOrder : currentOrder);
        return updatedOrder;
    };

    const handleConfirmCompletedOrder = async (order: Order) => {
        const response = await apiService.patch<{ data: Order }>(`/orders/${order._id}/confirm`, {});
        const updatedOrder = response.data;
        setOrders((currentOrders) => currentOrders.map((item) =>
            item._id === order._id ? updatedOrder : item,
        ));
        setSelectedOrder((currentOrder) => currentOrder?._id === order._id ? updatedOrder : currentOrder);
        return updatedOrder;
    };

    const handleCancelOrderConfirmation = async (order: Order) => {
        const response = await apiService.patch<{ data: Order }>(`/orders/${order._id}/confirm/cancel`, {});
        const updatedOrder = response.data;
        setOrders((currentOrders) => currentOrders.map((item) =>
            item._id === order._id ? updatedOrder : item,
        ));
        setSelectedOrder((currentOrder) => currentOrder?._id === order._id ? updatedOrder : currentOrder);
        return updatedOrder;
    };

    const handleRequestOrderRefund = async (order: Order, reason: string) => {
        const response = await socketService.requestCompletedOrderRefund(order._id, reason);
        if (!response.order) throw new Error('Máy chủ không trả về thông tin yêu cầu hoàn tiền.');
        const updatedOrder = response.order;
        setOrders((currentOrders) => currentOrders.map((item) =>
            item._id === order._id ? updatedOrder : item,
        ));
        setSelectedOrder((currentOrder) => currentOrder?._id === order._id ? updatedOrder : currentOrder);
        return updatedOrder;
    };

    const handleApproveOrderRedo = async (orderOrId: Order | string) => {
        const orderId = typeof orderOrId === 'string' ? orderOrId : orderOrId._id;
        const response = await socketService.approveCompletedOrderRedo(orderId);
        if (!response.order) throw new Error('Máy chủ không trả về đơn hàng làm lại.');
        const redoOrder = response.order;
        setOrders((currentOrders) => [
            redoOrder,
            ...currentOrders.map((item) => item._id === orderId
                ? { ...item, redoRequestStatus: 'approved' as const }
                : item),
        ].slice(0, 50));
        setSelectedOrder((currentOrder) => currentOrder?._id === orderId
            ? { ...currentOrder, redoRequestStatus: 'approved' }
            : currentOrder);
    };

    const handleApproveOrderRefund = async (orderOrId: Order | string) => {
        const orderId = typeof orderOrId === 'string' ? orderOrId : orderOrId._id;
        const response = await socketService.approveCompletedOrderRefund(orderId);
        if (!response.order) throw new Error('Máy chủ không trả về đơn hàng đã hoàn tiền.');
        const updatedOrder = response.order;
        setOrders((currentOrders) => currentOrders.map((item) =>
            item._id === orderId ? updatedOrder : item,
        ));
        setSelectedOrder((currentOrder) => currentOrder?._id === orderId ? updatedOrder : currentOrder);
    };

    const handleApproveRedoFromMessage = async (message: Message) => {
        if (!message.orderId || !message._id) return;
        const messageId = message._id;
        setApprovingRedoMessageId(message._id);
        setMessageActionError('');
        try {
            await handleApproveOrderRedo(message.orderId);
            setApprovedRedoMessageIds((currentIds) => [...currentIds, messageId]);
        } catch (error) {
            setMessageActionError(error instanceof Error ? error.message : 'Không thể duyệt yêu cầu làm lại.');
        } finally {
            setApprovingRedoMessageId(null);
        }
    };

    const handleApproveRefundFromMessage = async (message: Message) => {
        if (!message.orderId || !message._id) return;
        const messageId = message._id;
        setApprovingRefundMessageId(messageId);
        setMessageActionError('');
        try {
            await handleApproveOrderRefund(message.orderId);
            setApprovedRefundMessageIds((currentIds) => [...currentIds, messageId]);
        } catch (error) {
            setMessageActionError(error instanceof Error ? error.message : 'Không thể duyệt yêu cầu hoàn tiền.');
        } finally {
            setApprovingRefundMessageId(null);
        }
    };

    const handleToggleMessageReaction = async (message: Message, emoji: string) => {
        if (!message._id || reactingMessageId) return;
        setReactingMessageId(message._id);
        setMessageActionError('');
        try {
            await socketService.toggleMessageReaction(message._id, emoji);
            setActiveReactionMessageId(null);
        } catch (error) {
            setMessageActionError(error instanceof Error ? error.message : 'Không thể cập nhật cảm xúc.');
        } finally {
            setReactingMessageId(null);
        }
    };

    const handleToggleIntegrationPriority = async (order: Order) => {
        setUpdatingPriorityOrderId(order._id);
        try {
            const response = await apiService.patch<{ data: Order }>(
                `/orders/${order._id}/integration-priority`,
                { enabled: !order.integrationPriority },
            );
            setOrders((currentOrders) => currentOrders.map((item) =>
                item._id === order._id ? response.data : item,
            ));
            setSelectedOrder((currentOrder) => currentOrder?._id === order._id ? response.data : currentOrder);
            refreshPartnerOutstanding();
        } catch (error: any) {
            window.alert(error.response?.data?.message || 'Không thể cập nhật ưu tiên đơn hàng.');
        } finally {
            setUpdatingPriorityOrderId(null);
        }
    };

    const handleUpdatePendingOrder = async (order: Order, code: string, totalSteps: number) => {
        const response = await apiService.patch<{ data: Order }>(
            `/orders/${order._id}/pending-details`,
            { code, totalSteps },
        );
        setOrders((currentOrders) => currentOrders.map((item) =>
            item._id === order._id ? response.data : item,
        ));
        setSelectedOrder((currentOrder) => currentOrder?._id === order._id ? response.data : currentOrder);
        return response.data;
    };

    const handleSelectOrder = (order: Order) => {
        console.log('📦 Selecting order:', order._id, order.code);

        setSelectedOrder(order);
        setMessages(prev => sortSelectedOrderMessages(prev, order._id));

        const currentUserRoomId = `user_${String(currentUserId)}`;
        socketService.getOrderStatus(order._id);

        setTimeout(() => {
            const orderMessages = document.querySelectorAll('[data-order-id="' + order._id + '"]');
            const latestOrderMessage = orderMessages.length ? orderMessages[orderMessages.length - 1] : null;
            if (latestOrderMessage) {
                (latestOrderMessage as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 350);

        console.log('📦 Order selected, loading history from room:', currentUserRoomId);
    };

    const handleOpenOrderFromTable = (order: Order) => {
        setSelectedOrder(order);
        socketService.getOrderStatus(order._id);
    };

    const copySelectedOrderCode = async () => {
        if (!selectedOrder) return;
        try {
            await navigator.clipboard.writeText(selectedOrder.code);
            setIsOrderCodeCopied(true);
            window.setTimeout(() => setIsOrderCodeCopied(false), 1500);
        } catch (error) {
            console.error('Không sao chép được mã đơn hàng:', error);
            window.alert('Không thể sao chép mã đơn hàng. Vui lòng thử lại.');
        }
    };

    const getStatusColor = (status: OrderStatus) => {
        const colors = {
            [OrderStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
            [OrderStatus.CODE_RECEIVED]: 'bg-indigo-100 text-indigo-800',
            [OrderStatus.PROCESSING]: 'bg-blue-100 text-blue-800',
            [OrderStatus.COMPLETED]: 'bg-green-100 text-green-800',
            [OrderStatus.ERROR]: 'bg-red-100 text-red-800',
            [OrderStatus.CANCELED]: 'bg-gray-100 text-gray-700',
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getStatusEmoji = (status: OrderStatus) => {
        const emojis = {
            [OrderStatus.PENDING]: '⏳',
            [OrderStatus.CODE_RECEIVED]: '📌',
            [OrderStatus.PROCESSING]: '🔄',
            [OrderStatus.COMPLETED]: '✅',
            [OrderStatus.ERROR]: '❌',
            [OrderStatus.CANCELED]: '🚫',
        };
        return emojis[status] || '📋';
    };

    return (
        <div
            className="relative flex h-[100dvh] overflow-hidden bg-gray-100"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            {activeNav === 'home' && (isAdmin ? (
                <div className={`${isSidebarOpen ? 'fixed inset-y-0 left-0 z-30 flex md:flex' : 'hidden'} w-80 shrink-0 flex-col border-r border-gray-200 bg-white md:static md:z-auto`}>
                    <div className="p-4 border-b border-gray-200">
                        <div className="flex items-center justify-between gap-2">
                            <h2 className="text-lg font-semibold">💬 Phòng trò chuyện</h2>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsConversationSearchOpen((open) => !open);
                                        setConversationSearch('');
                                        setConversationSearchResults([]);
                                        setConversationSearchError('');
                                    }}
                                    className={`rounded-lg p-2 text-gray-600 hover:bg-gray-100 ${isConversationSearchOpen ? 'bg-blue-50 text-blue-700' : ''}`}
                                    aria-label={isConversationSearchOpen ? 'Đóng tìm người dùng' : 'Tìm người dùng'}
                                    title={isConversationSearchOpen ? 'Đóng tìm kiếm' : 'Tìm người dùng'}
                                >
                                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
                                        <circle cx="11" cy="11" r="7" />
                                        <path d="m20 20-4-4" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsSidebarOpen(false)}
                                    className="rounded-lg px-2 py-1 text-xl leading-none text-gray-500 hover:bg-gray-100"
                                    aria-label="Đóng danh sách"
                                    title="Đóng danh sách"
                                >
                                    ‹
                                </button>
                            </div>
                        </div>
                        {isConversationSearchOpen && (
                            <input
                                autoFocus
                                type="search"
                                value={conversationSearch}
                                onChange={(event) => setConversationSearch(event.target.value)}
                                placeholder="Tìm theo tên, username hoặc ID"
                                aria-label="Tìm người dùng trong tin nhắn"
                                className="mt-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                            />
                        )}
                    </div>
                    <div
                        ref={conversationListRef}
                        onScroll={conversationSearch.trim().length >= 2 ? undefined : handleConversationListScroll}
                        className="flex-1 overflow-y-auto p-2"
                    >
                        {isConversationSearchOpen && conversationSearch.trim().length === 1 ? (
                            <div className="p-4 text-sm text-gray-500">Nhập ít nhất 2 ký tự để tìm.</div>
                        ) : isConversationSearchOpen && conversationSearch.trim().length >= 2 ? (
                            isSearchingConversations ? (
                                <div className="p-4 text-sm text-gray-500">Đang tìm người dùng...</div>
                            ) : conversationSearchError ? (
                                <div role="alert" className="p-4 text-sm text-red-600">{conversationSearchError}</div>
                            ) : conversationSearchResults.length === 0 ? (
                                <div className="p-4 text-sm text-gray-500">Không tìm thấy người dùng.</div>
                            ) : (
                                conversationSearchResults.map((result) => (
                                    <button
                                        key={result._id}
                                        type="button"
                                        onClick={() => handleSelectConversationUser(result)}
                                        className="mb-2 w-full rounded-lg border border-transparent bg-gray-50 p-3 text-left transition-all hover:border-blue-200 hover:bg-blue-50"
                                    >
                                        <span className="block truncate font-semibold text-gray-900">
                                            {result.fullName || result.username}
                                        </span>
                                        <span className="mt-1 block truncate text-xs text-gray-500">
                                            @{result.username} · {getRoleLabel(result.role)} · ID: {result._id}
                                        </span>
                                    </button>
                                ))
                            )
                        ) : conversations.length === 0 ? (
                            <div className="p-4 text-sm text-gray-500">
                                {isLoadingMoreConversations ? 'Đang tải cuộc trò chuyện...' : 'Chưa có cuộc trò chuyện nào'}
                            </div>
                        ) : (
                            <>
                                {conversations.map((room) => (
                                    <button
                                        key={room.roomId}
                                        type="button"
                                        onClick={() => handleSelectRoom(room.roomId)}
                                        className={`w-full text-left p-3 mb-2 rounded-lg transition-all ${
                                            activeRoomId === room.roomId
                                                ? 'bg-blue-50 border border-blue-200'
                                                : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold truncate">
                                                        {room.participantName || room.participantUsername || 'Người dùng'}
                                                    </span>
                                                    <span className="shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-gray-600">
                                                        {getRoleLabel(room.participantRole)}
                                                    </span>
                                                </div>
                                                <span className="block truncate text-xs text-gray-400" title={room.participantId || room.roomId}>
                                                    ID: {room.participantId || room.roomId}
                                                </span>
                                            </div>
                                        {(room.unreadCount || 0) > 0 ? (
                                            <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                                                {room.unreadCount}
                                            </span>
                                        ) : null}
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1 truncate">
                                        {room.lastMessage || 'Chưa có tin nhắn nào'}
                                    </p>
                                    </button>
                                ))}
                                {isLoadingMoreConversations && (
                                    <p className="p-3 text-center text-xs text-gray-500">Đang tải thêm...</p>
                                )}
                                {!hasMoreConversations && conversations.length > 0 && (
                                    <p className="p-3 text-center text-xs text-gray-400">Đã tải hết cuộc trò chuyện</p>
                                )}
                            </>
                        )}
                    </div>
                </div>
            ) : (
                <div className={`${isSidebarOpen ? 'fixed inset-y-0 left-0 z-30 flex md:flex' : 'hidden'} w-80 shrink-0 flex-col border-r border-gray-200 bg-white md:static md:z-auto`}>
                    <div className="p-4 border-b border-gray-200">
                        <div className="flex items-center justify-between gap-2">
                            <h2 className="text-lg font-semibold">📋 Đơn hàng</h2>
                            <button
                                type="button"
                                onClick={() => setIsSidebarOpen(false)}
                                className="rounded-lg px-2 py-1 text-xl leading-none text-gray-500 hover:bg-gray-100"
                                aria-label="Đóng danh sách"
                                title="Đóng danh sách"
                            >
                                ‹
                            </button>
                        </div>
                        <div className="mt-3 grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                            <div className="relative col-span-2">
                                <textarea
                                    value={orderCode}
                                    onChange={(event) => {
                                        setOrderCode(event.target.value);
                                        setOrderEntryError('');
                                        setOrderEntryMessage('');
                                    }}
                                    placeholder="Mã đơn hàng hoặc mã|số lượng\nVí dụ: bYG3qj5LuZ|2"
                                    rows={3}
                                    aria-label="Mã đơn hàng và số lần"
                                    className="min-w-0 w-full resize-y rounded-lg border border-gray-200 px-3 py-2 pr-10 font-mono text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                    type="button"
                                    ref={bulkOrderHelpButtonRef}
                                    onClick={() => setShowBulkOrderHelp((visible) => !visible)}
                                    className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 text-xs font-bold text-gray-500 hover:border-blue-500 hover:text-blue-600"
                                    aria-label="Cách nhập nhiều đơn hàng"
                                    title="Cách nhập nhiều đơn hàng"
                                >
                                    ?
                                </button>
                                {showBulkOrderHelp && (
                                    <div ref={bulkOrderHelpPopupRef} className="absolute left-0 right-0 top-full z-20 mt-1 rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-800 shadow-lg">
                                        <p className="font-semibold">Cách nhập nhiều đơn hàng</p>
                                        <p className="mt-1">Mỗi dòng nhập một mã dài đúng 10 ký tự. Có thể dán nguyên văn tin nhắn; mã sẽ được lấy từ phần nằm giữa hai dấu --.</p>
                                        <p className="mt-1">Số lượng sau | là tùy chọn; nếu bỏ qua thì mặc định là 1.</p>
                                        <p className="mt-1 font-mono">Mã mời của tôi là --bYG3qj5LuZ--</p>
                                        <p className="font-mono">bYG3qj5LuZ</p>
                                        <p className="font-mono">bYG3qj5LuZ|2</p>
                                    </div>
                                )}
                            </div>
                            <textarea
                                value={orderNote}
                                onChange={(event) => setOrderNote(event.target.value)}
                                maxLength={25}
                                rows={2}
                                aria-label="Ghi chú chung cho các đơn hàng"
                                placeholder="Ghi chú (Tên đơn hàng tối đa 25 ký tự)"
                                className="col-span-2 min-w-0 w-full resize-y rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                            />
                            <select
                                value={orderSteps}
                                onChange={(event) => setOrderSteps(Number(event.target.value))}
                                className="min-w-0 rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                                aria-label="Số lần"
                            >
                                {orderPricing.length > 0 ? orderPricing.map((item) => (
                                    <option key={item.totalSteps} value={item.totalSteps}>
                                        {item.title ? `${item.title} · ` : ''}{item.totalSteps} lần · {item.discountPercent ? `${(item.originalPrice ?? item.price).toLocaleString('vi-VN')} → ${(item.discountedPrice ?? item.price).toLocaleString('vi-VN')}` : item.price.toLocaleString('vi-VN')} Like{getPricingBonusLabel(item)}
                                    </option>
                                )) : <option value={1}>Chưa có bảng giá</option>}
                            </select>
                            <button
                                type="button"
                                onClick={handleCreateOrder}
                                disabled={isLoading}
                                className="min-w-[72px] rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {isLoading ? 'Đang tạo...' : 'Tạo'}
                            </button>
                            {selectedOrderPricing && (
                                <p className="col-span-2 text-xs text-gray-600">
                                    Giá đơn:{' '}
                                    {selectedOrderPricing.discountPercent ? (
                                        <>
                                            <del className="mr-1 text-gray-400">{(selectedOrderPricing.originalPrice ?? selectedOrderPricing.price).toLocaleString('vi-VN')} Like</del>
                                            <strong className="text-green-700">{(selectedOrderPricing.discountedPrice ?? selectedOrderPricing.price).toLocaleString('vi-VN')} Like</strong>
                                            <span className="ml-1 text-green-700">({orderDiscountTitle || 'Ưu đãi'} -{selectedOrderPricing.discountPercent}%)</span>
                                        </>
                                    ) : <strong>{selectedOrderPricing.price.toLocaleString('vi-VN')} Like</strong>}
                                </p>
                            )}
                            {selectedOrderPricing && (selectedOrderPricing.bonusSteps ?? 0) > 0 && <p className="col-span-2 text-xs font-medium text-emerald-700">Mua {selectedOrderPricing.totalSteps} lần{getPricingBonusLabel(selectedOrderPricing)}. Tổng nhận {(selectedOrderPricing.totalTickets ?? (selectedOrderPricing.totalSteps + (selectedOrderPricing.bonusSteps ?? 0)) * 38).toLocaleString('vi-VN')} vé.</p>}
                            {orderPriceEstimate && (
                                <p className="col-span-2 text-xs text-gray-600">
                                    Tổng tiền ước tính ({orderPriceEstimate.orderCount} đơn):{' '}
                                    {orderPriceEstimate.originalTotal !== orderPriceEstimate.payableTotal && (
                                        <del className="mr-1 text-gray-400">{orderPriceEstimate.originalTotal.toLocaleString('vi-VN')} Like</del>
                                    )}
                                    <strong className={orderPriceEstimate.originalTotal !== orderPriceEstimate.payableTotal ? 'text-green-700' : ''}>
                                        {orderPriceEstimate.payableTotal.toLocaleString('vi-VN')} Like
                                    </strong>
                                </p>
                            )}
                            {(orderEntryError || orderEntryMessage) && (
                                <p role={orderEntryError ? 'alert' : 'status'} className={`col-span-2 text-xs ${orderEntryError ? 'text-red-600' : 'text-green-700'}`}>
                                    {orderEntryError || orderEntryMessage}
                                </p>
                            )}
                        </div>
                    </div>
                    <div
                        ref={orderListRef}
                        onScroll={handleOrderListScroll}
                        className="flex-1 overflow-y-auto p-2"
                    >
                        {orders.length === 0 && isLoadingOrderPage ? (
                            <div className="p-4 text-center text-sm text-gray-500">Đang tải đơn hàng...</div>
                        ) : null}
                        {orders.map((order) => (
                            <div
                                key={order._id}
                                onClick={() => handleSelectOrder(order)}
                                className={`p-3 mb-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                                    selectedOrder?._id === order._id
                                        ? 'bg-blue-50 border-2 border-blue-300'
                                        : 'bg-gray-50 hover:bg-gray-100'
                                }`}
                            >
                                <div className="flex justify-between items-center">
                                    <span className="font-medium">{order.code}</span>
                                    <div className={`px-2 py-1 rounded-full text-xs ${getStatusColor(order.status)}`}>
                                        {getStatusEmoji(order.status)} {getOrderStatusLabel(order.status)}
                                    </div>
                                </div>
                                {order.note && (
                                    <p className="mt-1 truncate text-xs text-gray-600" title={order.note}>
                                        Ghi chú: {order.note}
                                    </p>
                                )}
                                <div className="flex justify-between items-center mt-1 text-sm text-gray-500">
                                    <span>Tiến độ: {order.progress}%</span>
                                    <span>Lần {order.currentStep}/{order.totalSteps}</span>
                                </div>
                                <div className="mt-1 text-xs text-gray-500">
                                    Đã chạy: {order.processedSteps || order.currentStep || 0} · Thành công: {Math.max(0, Math.max(order.processedSteps || 0, order.currentStep || 0) - (order.failedSteps || 0))} · Lỗi: {order.failedSteps || 0}
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                                    <div
                                        className={`h-1.5 rounded-full transition-all duration-500 ${
                                            order.status === OrderStatus.ERROR ? 'bg-red-500' :
                                            order.status === OrderStatus.COMPLETED ? 'bg-green-500' :
                                            order.status === OrderStatus.CANCELED ? 'bg-gray-500' :
                                            'bg-blue-500'
                                        }`}
                                        style={{ width: `${order.progress}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                        {ordersLoadError ? (
                            <div className="p-3 text-center text-sm text-red-600">
                                <p>{ordersLoadError}</p>
                                <button
                                    type="button"
                                    onClick={() => void loadOrderPage(retryOrderPageFromStartRef.current)}
                                    className="mt-1 font-medium underline"
                                >
                                    Thử lại
                                </button>
                            </div>
                        ) : isLoadingOrderPage && orders.length > 0 ? (
                            <div className="p-3 text-center text-sm text-gray-500">Đang tải thêm đơn hàng...</div>
                        ) : !hasMoreOrders && orders.length > 0 ? (
                            <div className="p-3 text-center text-xs text-gray-400">Đã tải hết đơn hàng</div>
                        ) : null}
                    </div>
                </div>
            ))}

            {/* Main Chat Area */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                {/* Header */}
                <div className="bg-white border-b border-gray-200">
                    <div className="flex min-w-0 items-center justify-between gap-2 px-2 py-2 sm:px-4 sm:py-3">
                        {activeNav === 'home' ? (
                            <button
                                type="button"
                                onClick={() => setIsSidebarOpen((open) => !open)}
                                className="w-10 shrink-0 rounded-lg px-2 py-1 text-2xl leading-none text-gray-600 hover:bg-gray-100"
                                aria-label={isSidebarOpen ? 'Đóng danh sách' : 'Mở danh sách'}
                                title={isSidebarOpen ? 'Đóng danh sách' : 'Mở danh sách'}
                            >
                                {isSidebarOpen ? '‹' : '›'}
                            </button>
                        ) : <div className="w-10 shrink-0" />}
                        <nav
                            ref={navigationRef}
                            aria-label="Điều hướng chính"
                            className="relative flex min-w-0 flex-1 flex-nowrap items-center justify-start gap-1 overflow-x-auto overscroll-x-contain py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:flex-wrap md:justify-center md:gap-2 md:overflow-visible"
                        >
                            {[
                                { key: 'home', label: 'Tin nhắn' },
                                { key: 'orders', label: 'Đơn hàng' },
                                ...(isAdmin || skinCollabEventsEnabled ? [{ key: 'skin-collab-events', label: 'Quay skin hợp tác' }] : []),
                                { key: 'articles', label: 'Bài viết' },
                                ...(!isAdmin ? [{ key: 'recharge', label: 'Nạp tiền' }] : []),
                                ...(isAdmin ? [{ key: 'admin-recharge', label: 'Duyệt nạp tiền' }] : []),
                                ...(isAdmin ? [{ key: 'admin-pricing', label: 'Quản lý giá' }] : []),
                                ...(isAdmin ? [{ key: 'admin-coupons', label: 'Mã giảm giá' }] : []),
                                ...(isAdmin ? [{ key: 'admin-statistics', label: 'Thống kê user' }] : []),
                                ...(isAdmin ? [{ key: 'admin-maintenance', label: 'Bảo trì' }] : []),
                                ...(isAdmin ? [{ key: 'admin-clone-accounts', label: 'QL acc clone' }] : []),
                            ].map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    aria-current={activeNav === item.key ? 'page' : undefined}
                                    onClick={() => {
                                        setActiveNav(item.key as ChatNavigation);
                                        if (item.key !== 'home' && item.key !== 'orders') setSelectedOrder(null);
                                    }}
                                    className={`shrink-0 whitespace-nowrap px-3 py-2 rounded-lg text-sm font-medium transition ${
                                        activeNav === item.key
                                            ? 'bg-blue-500 text-white shadow-sm'
                                            : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </nav>

                        <div className="relative shrink-0" ref={accountMenuRef}>
                            <button
                                type="button"
                                onClick={() => {
                                    const nextOpen = !isAccountMenuOpen;
                                    setIsAccountMenuOpen(nextOpen);
                                    if (nextOpen && !isAdmin) void loadRechargeHistory();
                                }}
                                className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gradient-to-br from-blue-500 to-indigo-600 text-sm font-semibold text-white shadow-sm transition hover:shadow-md"
                                aria-label="Mở menu tài khoản"
                            >
                                <img
                                    src={user?.avatar || '/avatarMLX.png'}
                                    alt={displayName}
                                    className="h-full w-full object-cover"
                                />
                            </button>

                            {isAccountMenuOpen && (
                                <div className="absolute right-0 z-20 mt-2 w-80 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
                                    <div className="border-b border-gray-200 p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-sm font-bold text-white">
                                                <img
                                                    src={user?.avatar || '/avatarMLX.png'}
                                                    alt={displayName}
                                                    className="h-full w-full object-cover"
                                                />
                                            </div>
                                            <div>
                                                <p className="font-semibold text-gray-800">{displayName}</p>
                                                <p className="text-xs text-gray-500">@{user?.username}</p>
                                                <p className="text-xs text-gray-500">{getRoleLabel(user?.role)}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsChangePasswordOpen(true);
                                                setIsAccountMenuOpen(false);
                                                setPasswordError('');
                                                setPasswordMessage('');
                                            }}
                                            className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                        >
                                            <span>Thông tin tài khoản / Đổi mật khẩu</span>
                                            <span>👤</span>
                                        </button>

                                        <div className="mt-2 rounded-xl bg-gray-50 p-2">
                                            <div className="mb-2 flex items-center justify-between px-2 py-1">
                                                <span className="text-sm font-medium text-gray-700">Lịch sử nạp tiền</span>
                                                <span className="text-xs text-blue-500">{rechargeHistory.length} giao dịch</span>
                                            </div>
                                            <div className="space-y-2">
                                                {isRechargeHistoryLoading ? (
                                                    <p className="px-2 py-3 text-xs text-gray-500">Đang tải lịch sử...</p>
                                                ) : rechargeHistory.length === 0 ? (
                                                    <p className="px-2 py-3 text-xs text-gray-500">Chưa có giao dịch nạp tiền</p>
                                                ) : rechargeHistory.map((item) => (
                                                    <div
                                                        key={item.transactionId}
                                                        className="flex items-center justify-between rounded-lg bg-white px-2 py-2 shadow-sm"
                                                    >
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-700">{item.amount.toLocaleString('vi-VN')} Like</p>
                                                            <p className="text-[11px] text-gray-400">{formatRechargeDate(item.createdAt)}</p>
                                                        </div>
                                                        <span className={`rounded-full px-2 py-1 text-[10px] font-medium ${
                                                            item.status === 'success'
                                                                ? 'bg-green-100 text-green-700'
                                                                : item.status === 'failed' || item.status === 'cancelled' || item.status === 'expired'
                                                                    ? 'bg-red-100 text-red-700'
                                                                    : 'bg-yellow-100 text-yellow-700'
                                                        }`}>
                                                            {getRechargeStatusLabel(item.status)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={handleLogout}
                                            className="mt-3 flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-medium text-red-600 hover:bg-red-50"
                                        >
                                            <span>Đăng xuất</span>
                                            <span>↩</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 px-3 pb-3 md:px-4 md:pb-4">
                        <div>
                            <div className="flex items-center gap-1.5">
                                <h1 className="text-[14px] font-semibold md:text-xl">
                                    {selectedOrder
                                        ? `Đơn hàng: ${selectedOrder.code}`
                                        : activeNav === 'home' ? (isAdmin ? 'Tin nhắn' : activeRoomLabel) : activeNav === 'orders' ? 'Đơn hàng' : activeNav === 'articles' ? 'Bài viết' : activeNav === 'admin-recharge' ? 'Duyệt nạp tiền' : activeNav === 'admin-pricing' ? 'Quản lý giá' : activeNav === 'admin-coupons' ? 'Mã giảm giá' : activeNav === 'admin-statistics' ? 'Thống kê user' : activeNav === 'admin-maintenance' ? 'Bảo trì' : activeNav === 'admin-clone-accounts' ? 'QL acc clone' : activeNav === 'skin-collab-events' ? 'Quay skin hợp tác' : 'Nạp tiền'}
                                </h1>
                                {selectedOrder && (
                                    <button
                                        type="button"
                                        onClick={() => void copySelectedOrderCode()}
                                        aria-label={`Sao chép mã đơn ${selectedOrder.code}`}
                                        title={isOrderCodeCopied ? 'Đã sao chép' : 'Sao chép mã đơn'}
                                        className="inline-flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-blue-600"
                                    >
                                        {isOrderCodeCopied ? (
                                            <span aria-hidden="true" className="text-sm text-green-600">✓</span>
                                        ) : (
                                            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                                                <rect x="8" y="8" width="12" height="12" rx="2" />
                                                <path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
                                            </svg>
                                        )}
                                    </button>
                                )}
                            </div>
                            {selectedOrder && (
                                <>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(selectedOrder.status)}`}>
                                            {getStatusEmoji(selectedOrder.status)} {selectedOrder.error === 'Cancelled' ? 'Đã hủy' : getOrderStatusLabel(selectedOrder.status)}
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            {selectedOrder.progress}% · Lần {selectedOrder.currentStep}/{selectedOrder.totalSteps}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            Đã chạy: {selectedOrder.processedSteps ?? selectedOrder.currentStep ?? 0}
                                            {' · '}Thành công: {Math.max(0, Math.max(selectedOrder.processedSteps || 0, selectedOrder.currentStep || 0) - (selectedOrder.failedSteps || 0))}
                                            {' · '}Lỗi: {selectedOrder.failedSteps ?? 0}
                                        </span>
                                    </div>
                                    {selectedOrder.status === OrderStatus.ERROR && (selectedOrder.error || selectedOrder.message) && (
                                        <p role="status" className="mt-2 max-w-3xl rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                            Lý do thất bại: {selectedOrder.error || selectedOrder.message}
                                        </p>
                                    )}
                                    {(isAdmin || isPartner) && selectedOrder.status === OrderStatus.PENDING && (
                                        <button
                                            type="button"
                                            onClick={() => void handleToggleIntegrationPriority(selectedOrder)}
                                            disabled={updatingPriorityOrderId === selectedOrder._id}
                                            aria-label={selectedOrder.integrationPriority ? 'Bỏ ưu tiên đơn hàng' : 'Ưu tiên đơn hàng'}
                                            className={`mt-2 rounded-lg border px-3 py-2 text-sm font-semibold transition disabled:cursor-wait disabled:opacity-50 ${selectedOrder.integrationPriority ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100' : 'border-gray-200 bg-white text-gray-700 hover:border-amber-300 hover:text-amber-800'}`}
                                        >
                                            {updatingPriorityOrderId === selectedOrder._id
                                                ? 'Đang cập nhật...'
                                                : selectedOrder.integrationPriority ? '★ Đang ưu tiên' : '☆ Ưu tiên đơn này'}
                                        </button>
                                    )}
                                    {canCancelOrders && (isAdmin || selectedOrder.status === OrderStatus.PENDING) && (
                                        <button
                                            type="button"
                                            onClick={() => void handleCancelOrder(selectedOrder)}
                                            disabled={cancellingOrderId === selectedOrder._id}
                                            className="mt-2 ml-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-wait disabled:opacity-50"
                                            title={isAdmin ? 'Hủy đơn và nhập lý do' : 'Hủy đơn và áp dụng phí 5 Like'}
                                        >
                                            {cancellingOrderId === selectedOrder._id ? 'Đang hủy...' : 'Hủy đơn'}
                                        </button>
                                    )}
                                </>
                            )}
                            {activeNav === 'orders' && (
                                <>
                                    {unfinishedOrderCountError && (
                                        <p role="alert" className="mt-1 text-xs text-red-600">
                                            {unfinishedOrderCountError}
                                        </p>
                                    )}
                                    <p role="status" className="mt-1 text-xs text-gray-500">
                                        {!unfinishedOrderCountError && (
                                            <>
                                                Đơn hàng hệ thống đang chạy: {unfinishedOrderCount === null ? 'Đang tải...' : unfinishedOrderCount.toLocaleString('vi-VN')}
                                                {' · '}Đơn hàng đã thành công: {successfulOrderCount === null ? 'Đang tải...' : successfulOrderCount.toLocaleString('vi-VN')}
                                                {' · '}
                                            </>
                                        )}
                                        {isAdmin && (
                                            <>
                                                Acc clone còn lại: {cloneAccountPendingCount === null
                                                    ? cloneAccountPendingCountError ? 'Không tải được' : 'Đang tải...'
                                                    : cloneAccountPendingCount.toLocaleString('vi-VN')}
                                                {' · '}
                                            </>
                                        )}
                                        Người đang truy cập web: {onlineUserCount === null ? 'Đang tải...' : onlineUserCount.toLocaleString('vi-VN')}
                                    </p>
                                    {isAdmin && cloneAccountPendingCountError && (
                                        <p role="alert" className="mt-1 text-xs text-red-600">{cloneAccountPendingCountError}</p>
                                    )}
                                </>
                            )}
                            {activeNav === 'skin-collab-events' && (
                                <p role="status" className="mt-1 text-xs text-gray-500">
                                    Đơn đang chạy: {skinExplosionSummary === null ? 'Đang tải...' : skinExplosionSummary.runningOrders.toLocaleString('vi-VN')}
                                    {' · '}
                                    Tổng acc hệ thống: {skinExplosionSummary === null ? 'Đang tải...' : skinExplosionSummary.totalAccountsRun.toLocaleString('vi-VN')}
                                    {' · '}Tổng acc nổ skin: {skinExplosionSummary === null
                                        ? 'Đang tải...'
                                        : `${skinExplosionSummary.skinExplosionAccounts.toLocaleString('vi-VN')} (${skinExplosionSummary.totalAccountsRun > 0 ? (skinExplosionSummary.skinExplosionAccounts / skinExplosionSummary.totalAccountsRun * 100).toLocaleString('vi-VN', { maximumFractionDigits: 2 }) : '0'}%)`}
                                </p>
                            )}
                        </div>
                        {isPartner && outstandingAmount > 0 && !selectedOrder && (
                            <div className="flex shrink-0 items-center gap-2 rounded-lg bg-orange-50 px-2 py-1.5 text-right text-[11px] text-orange-700 md:gap-3 md:px-3 md:py-2 md:text-sm">
                                <div>
                                    <span className="block text-[10px] md:text-xs">Tổng chưa thanh toán</span>
                                    <strong className="text-[11px] md:text-sm">{outstandingAmount.toLocaleString('vi-VN')} Like</strong>
                                    <span className="block text-[10px] md:text-xs">{outstandingSteps.toLocaleString('vi-VN')} số lượt</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setActiveNav('recharge')}
                                    className="rounded-md bg-orange-500 px-2 py-1.5 text-[10px] font-semibold text-white hover:bg-orange-600 md:px-3 md:py-2 md:text-sm"
                                >
                                    Thanh toán
                                </button>
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            {selectedOrder && isAdmin && (
                                <>
                                    {selectedOrder.status === OrderStatus.ERROR && (selectedOrder.failedSteps || 0) > 0 && (
                                        <button
                                            onClick={() => handleRecreateOrder(selectedOrder)}
                                            className="px-3 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600"
                                            title="Tạo lại theo số lần lỗi"
                                        >
                                            🔁 Tạo lại ({selectedOrder.failedSteps} lần)
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleStepOrder(selectedOrder._id)}
                                        disabled={selectedOrder.status === OrderStatus.COMPLETED || selectedOrder.status === OrderStatus.ERROR || selectedOrder.status === OrderStatus.CANCELED}
                                        className="px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
                                        title="Chạy 1 lần"
                                    >
                                        ⏭️ Lần
                                    </button>
                                    <button
                                        onClick={() => handleProcessOrder(selectedOrder._id)}
                                        disabled={selectedOrder.status === OrderStatus.COMPLETED || selectedOrder.status === OrderStatus.ERROR || selectedOrder.status === OrderStatus.CANCELED}
                                        className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50"
                                        title="Chạy tất cả các lần"
                                    >
                                        ▶️ Chạy
                                    </button>
                                    <button
                                        onClick={() => handleResetOrder(selectedOrder._id)}
                                        disabled={selectedOrder.status === OrderStatus.COMPLETED}
                                        className="px-3 py-2 bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-600 disabled:opacity-50"
                                        title="Đặt lại đơn hàng"
                                    >
                                        🔄 Đặt lại
                                    </button>
                                </>
                            )}
                            <span
                                role="status"
                                aria-label={isConnected ? 'Đang kết nối' : 'Mất kết nối'}
                                title={isConnected ? 'Đang kết nối' : 'Mất kết nối'}
                                className="inline-flex h-5 w-5 items-center justify-center"
                            >
                                <span className={`h-2.5 w-2.5 rounded-full ${
                                    isConnected ? 'bg-green-500' : 'bg-red-500'
                                }`} />
                            </span>
                            {selectedOrder && (
                                <button
                                    onClick={() => setSelectedOrder(null)}
                                    aria-label="Bỏ chọn đơn hàng"
                                    className="flex h-8 w-8 items-center justify-center rounded-lg text-xl font-semibold leading-none text-red-600 hover:bg-red-50"
                                    title="Bỏ chọn đơn hàng"
                                >
                                    ×
                                </button>
                            )}
                           
                        </div>
                    </div>
                </div>

                {activeNav === 'recharge' ? <DepositPage /> : activeNav === 'admin-recharge' ? <AdminDepositPage /> : activeNav === 'admin-pricing' ? <AdminOrderPricingPage /> : activeNav === 'admin-coupons' ? <AdminCouponsPage /> : activeNav === 'admin-statistics' ? <AdminUserStatisticsPage /> : activeNav === 'admin-maintenance' ? <AdminMaintenancePage enabled={maintenanceEnabled} statusError={maintenanceStatusError} onRefresh={refreshMaintenanceStatus} onChange={(enabled) => { setMaintenanceEnabled(enabled); setMaintenanceStatusError(''); }} skinCollabEventsEnabled={skinCollabEventsEnabled} onSkinCollabEventsChange={(enabled) => setSkinCollabEventsEnabled(enabled)} /> : activeNav === 'admin-clone-accounts' ? <AdminCloneAccountsPage /> : activeNav === 'skin-collab-events' ? <SkinCollabEventsPage isAdmin={isAdmin} /> : activeNav === 'articles' ? <ArticlesPage isAdmin={isAdmin} /> : activeNav === 'orders' ? (
                    <OrderManagementPage
                        onSelectOrder={handleOpenOrderFromTable}
                        onToggleIntegrationPriority={handleToggleIntegrationPriority}
                        onCancelOrder={handleCancelOrder}
                        onUpdatePendingOrder={handleUpdatePendingOrder}
                        onRequestRedo={handleRequestOrderRedo}
                        onConfirmOrder={handleConfirmCompletedOrder}
                        onCancelOrderConfirmation={handleCancelOrderConfirmation}
                        onApproveRedo={handleApproveOrderRedo}
                        onRequestRefund={handleRequestOrderRefund}
                        onApproveRefund={handleApproveOrderRefund}
                        canPrioritizeOrders={isAdmin || isPartner}
                        canCancelOrders={canCancelOrders}
                        canEditPendingOrders={canCancelOrders}
                        canRequestRedo={user?.role === 'user' || user?.role === 'partner'}
                        canApproveRedo={isAdmin}
                        canRequestRefund={user?.role === 'user' || user?.role === 'partner'}
                        canApproveRefund={isAdmin}
                        orderCode={orderCode}
                        onOrderCodeChange={(value) => { setOrderCode(value); setOrderEntryError(''); setOrderEntryMessage(''); }}
                        couponCode={couponCode}
                        onCouponCodeChange={setCouponCode}
                        orderNote={orderNote}
                        onOrderNoteChange={setOrderNote}
                        orderSteps={orderSteps}
                        onOrderStepsChange={setOrderSteps}
                        orderPricing={orderPricing}
                        orderDiscountTitle={orderDiscountTitle}
                        onCreateOrder={handleCreateOrder}
                        isCreating={isLoading}
                        createError={orderEntryError}
                        createMessage={orderEntryMessage}
                        maintenanceEnabled={maintenanceEnabled}
                        canCreateOrder
                    />
                ) : <>
                {/* Messages */}
                <div className="relative min-h-0 flex-1">
                <div
                    ref={messageListRef}
                    onScroll={handleMessageListScroll}
                    className="h-full overflow-y-auto p-4 space-y-3"
                >
                    {isLoadingOlderMessages && (
                        <div className="py-2 text-center text-xs text-gray-500">Đang tải tin nhắn cũ hơn...</div>
                    )}
                    {messages.map((message, messageIndex) => {
                        const isOwnMessage = String(message.userId) === String(currentUserId);
                        const replyTarget = typeof message.replyTo === 'string'
                            ? messages.find((item) => item._id === message.replyTo || item.id === message.replyTo)
                            : message.replyTo;
                        const replyTargetId = typeof message.replyTo === 'string'
                            ? message.replyTo
                            : message.replyTo?._id;
                        const replyTargetName = replyTarget && 'username' in replyTarget
                            ? replyTarget.username
                            : replyTarget?.senderFullName || replyTarget?.senderUsername;
                        const replyTargetContent = typeof message.replyTo !== 'string' && message.replyTo?.isDeleted
                            ? 'Tin nhắn đã bị xóa'
                            : replyTarget?.content || 'Nội dung không còn trong lịch sử';
                        const previousMessage = messages[messageIndex - 1];
                        const isNewDay = !previousMessage
                            || !isSameCalendarDay(previousMessage.timestamp, message.timestamp);
                        const statusLabel = {
                            sent: 'Đã gửi',
                            delivered: 'Đã nhận',
                            read: 'Đã đọc',
                        }[message.status || 'sent'];
                        const reactionSummary = MESSAGE_REACTIONS.map(({ emoji, label }) => {
                            const matchingReactions = (message.reactions || []).filter((reaction) => reaction.emoji === emoji);
                            return {
                                emoji,
                                label,
                                count: matchingReactions.length,
                                selected: matchingReactions.some((reaction) => reaction.userId === currentUserId),
                            };
                        }).filter((reaction) => reaction.count > 0);

                        return (
                            <React.Fragment key={message.id}>
                                {isNewDay && (
                                    <div className="flex items-center justify-center py-2 text-xs text-gray-500">
                                        <span className="rounded-full bg-gray-200 px-3 py-1 font-medium">
                                            {formatMessageDay(message.timestamp)}
                                        </span>
                                    </div>
                                )}
                                <div
                                data-order-id={message.type === 'order' ? message.orderId : undefined}
                                ref={(el) => {
                                    if (el) {
                                        messageRefs.current[message._id || message.id] = el;
                                    }
                                }}
                                className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                                onClick={() => {
                                    // Admin can click on order messages to select and control order
                                    if (isAdmin && message.type === 'order' && message.orderId) {
                                        // First, try to find order from orders list
                                        let selectedOrd = orders.find(o => o._id === message.orderId);
                                        
                                        // If not found, create temporary order from message data
                                        if (!selectedOrd) {
                                            selectedOrd = {
                                                _id: message.orderId,
                                                code: message.content.match(/(?:Order|Đơn hàng) ([^:]+)/)?.[1] || 'Không xác định',
                                                userId: message.userId,
                                                username: message.username,
                                                status: (message.progress || 0) === 100 ? 'COMPLETED' : (message.progress || 0) > 0 ? 'PROCESSING' : 'PENDING',
                                                progress: message.progress || 0,
                                                currentStep: message.currentStep || 0,
                                                totalSteps: message.totalSteps || 8,
                                                createdAt: message.timestamp,
                                                updatedAt: message.timestamp,
                                            } as Order;
                                        }
                                        
                                        setSelectedOrder(selectedOrd);
                                        // Get latest order status from backend
                                        socketService.getOrderStatus(message.orderId);
                                    }
                                }}
                                >
                                <div className={`max-w-[70%] ${
                                    message.type === 'order'
                                        ? `bg-white border-2 border-blue-200 rounded-lg ${isAdmin ? 'cursor-pointer hover:shadow-lg hover:border-blue-400' : ''}`
                                        : isOwnMessage
                                            ? 'bg-blue-500 text-white rounded-l-xl rounded-tr-xl'
                                            : 'bg-white text-gray-800 rounded-r-xl rounded-tl-xl'
                                } p-3 shadow`}>
                                    {message.replyTo && (
                                        <button
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                scrollToMessage(replyTargetId);
                                            }}
                                            disabled={!replyTargetId}
                                            className={`mb-2 block w-full rounded-md border-l-2 px-2 py-1 text-left text-xs ${
                                                isOwnMessage
                                                    ? 'border-blue-200 bg-blue-600/50 text-blue-50'
                                                    : 'border-blue-400 bg-gray-50 text-gray-600'
                                            } disabled:cursor-default`}
                                            title="Tin nhắn được trả lời"
                                        >
                                            <span className="block font-semibold">
                                                {replyTargetName || 'Tin nhắn'}
                                            </span>
                                            <span className="block truncate">
                                                {replyTargetContent}
                                            </span>
                                        </button>
                                    )}
                                    {message.type === 'order' ? (
                                        // Order Progress Message
                                        <div className="text-gray-800">
                                            <div className="text-sm font-semibold mb-2 text-blue-600">{message.content}</div>
                                            {message.progress !== undefined && (
                                                <div>
                                                    {(() => {
                                                        const isOrderError = /(?:LỖI|ERROR)/i.test(message.content || '');
                                                        return (
                                                            <>
                                                    <div className="flex justify-between text-xs text-gray-600 mb-2">
                                                        <span>Tiến độ xử lý:</span>
                                                        <span className="font-semibold">{message.progress}% • Lần {message.currentStep}/{message.totalSteps}</span>
                                                    </div>
                                                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                                                        <div
                                                            className={`h-2.5 rounded-full transition-all duration-500 ${
                                                                isOrderError ? 'bg-red-500' : message.progress === 100 ? 'bg-green-500' :
                                                                message.progress >= 75 ? 'bg-blue-500' :
                                                                message.progress >= 50 ? 'bg-yellow-500' :
                                                                'bg-orange-500'
                                                            }`}
                                                            style={{ width: `${message.progress}%` }}
                                                        />
                                                    </div>
                                                    <div className="mt-2 text-xs text-gray-500">
                                                        {message.orderStatus && `Trạng thái: ${getOrderStatusLabel(message.orderStatus)} · `}
                                                        {isOrderError && '❌ Đơn hàng bị lỗi, cần tạo lại lần lỗi'}
                                                        {!isOrderError && message.progress === 0 && '⏳ Đang chờ xử lý...'}
                                                        {!isOrderError && message.progress > 0 && message.progress < 100 && '🔄 Đang xử lý...'}
                                                        {!isOrderError && message.progress === 100 && '✅ Hoàn thành!'}
                                                    </div>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                            {isAdmin && (
                                                <div className="mt-2 text-xs text-gray-500 italic">
                                                    💡 Nhấn để kiểm soát order
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <div className="text-sm">{message.content}</div>
                                            {isOwnMessage && statusLabel && (
                                                <div className={`mt-1 flex justify-end text-[10px] ${
                                                    isOwnMessage ? 'text-blue-100' : 'text-gray-400'
                                                }`}>
                                                    <span>{statusLabel}</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {message._id && message.type !== 'system' && (
                                        <div className={`mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] ${
                                            isOwnMessage ? 'text-blue-100' : 'text-gray-500'
                                        }`} onClick={(event) => event.stopPropagation()}>
                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setReplyingTo(message);
                                                    setMessageActionError('');
                                                }}
                                                className="hover:underline"
                                            >
                                                ↩ Trả lời
                                            </button>
                                            {isAdmin && !isOwnMessage && message.userId !== 'system' && (
                                                <button
                                                    type="button"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        void handleMarkMessageUnread(message);
                                                    }}
                                                    disabled={markingUnreadId === message._id}
                                                    className="hover:underline disabled:opacity-50"
                                                >
                                                    {markingUnreadId === message._id ? 'Đang cập nhật...' : 'Đánh dấu chưa đọc'}
                                                </button>
                                            )}
                                            {isAdmin
                                                && !isOwnMessage
                                                && message.orderId
                                                && message.content.startsWith('🔁 Yêu cầu làm lại đơn hàng ')
                                                && !approvedRedoMessageIds.includes(message._id)
                                                && (
                                                    <button
                                                        type="button"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            void handleApproveRedoFromMessage(message);
                                                        }}
                                                        disabled={approvingRedoMessageId === message._id}
                                                        className="font-semibold text-green-700 hover:underline disabled:opacity-50"
                                                    >
                                                        {approvingRedoMessageId === message._id ? 'Đang duyệt...' : 'Duyệt làm lại miễn phí'}
                                                    </button>
                                                )}
                                            {isAdmin
                                                && !isOwnMessage
                                                && message.orderId
                                                && message.content.startsWith('💸 Yêu cầu hoàn tiền đơn hàng ')
                                                && message._id
                                                && !approvedRefundMessageIds.includes(message._id)
                                                && (
                                                    <button
                                                        type="button"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            void handleApproveRefundFromMessage(message);
                                                        }}
                                                        disabled={approvingRefundMessageId === message._id}
                                                        className="font-semibold text-green-700 hover:underline disabled:opacity-50"
                                                    >
                                                        {approvingRefundMessageId === message._id ? 'Đang duyệt...' : 'Duyệt hoàn tiền'}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                                                {reactionSummary.map((reaction) => (
                                                    <button
                                                        key={reaction.emoji}
                                                        type="button"
                                                        onClick={() => void handleToggleMessageReaction(message, reaction.emoji)}
                                                        disabled={reactingMessageId === message._id}
                                                        aria-label={`${reaction.label}, ${reaction.count} cảm xúc${reaction.selected ? ', bạn đã chọn' : ''}`}
                                                        aria-pressed={reaction.selected}
                                                        title={reaction.label}
                                                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition disabled:opacity-50 ${
                                                            reaction.selected
                                                                ? 'border-blue-400 bg-blue-50 text-blue-700'
                                                                : 'border-gray-200 bg-white/90 text-gray-700 hover:border-blue-300'
                                                        }`}
                                                    >
                                                        <span>{reaction.emoji}</span>
                                                        <span>{reaction.count}</span>
                                                    </button>
                                                ))}
                                                <div className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => setActiveReactionMessageId((currentId) => currentId === message._id ? null : message._id || null)}
                                                        disabled={reactingMessageId === message._id}
                                                        aria-label="Thêm cảm xúc"
                                                        aria-expanded={activeReactionMessageId === message._id}
                                                        title="Thêm cảm xúc"
                                                        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white/90 text-sm text-gray-600 transition hover:border-blue-300 hover:text-blue-600 disabled:opacity-50"
                                                    >
                                                        ☺+
                                                    </button>
                                                    {activeReactionMessageId === message._id && (
                                                        <div className="absolute bottom-full right-0 z-30 mb-2 flex gap-1 rounded-full border border-gray-200 bg-white p-1.5 shadow-lg">
                                                            {MESSAGE_REACTIONS.map((reaction) => (
                                                                <button
                                                                    key={reaction.emoji}
                                                                    type="button"
                                                                    onClick={() => void handleToggleMessageReaction(message, reaction.emoji)}
                                                                    disabled={reactingMessageId === message._id}
                                                                    aria-label={reaction.label}
                                                                    title={reaction.label}
                                                                    className="flex h-9 w-9 items-center justify-center rounded-full text-xl transition hover:scale-125 hover:bg-gray-100 disabled:opacity-50"
                                                                >
                                                                    {reaction.emoji}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className={`mt-2 text-[10px] ${
                                        isOwnMessage ? 'text-blue-100' : 'text-gray-400'
                                    }`}>
                                        {formatMessageTime(message.timestamp)}
                                    </div>
                                </div>
                                </div>
                            </React.Fragment>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>
                {showScrollToLatest && (
                    <button
                        type="button"
                        onClick={scrollToLatestMessage}
                        aria-label="Cuộn xuống tin nhắn mới nhất"
                        title="Tin nhắn mới nhất"
                        className="absolute bottom-4 right-5 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-lg transition hover:bg-gray-50"
                    >
                        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
                            <path d="M12 4v15" />
                            <path d="m6 13 6 6 6-6" />
                        </svg>
                    </button>
                )}
                </div>

                {/* Input */}
                <div className="sticky bottom-0 z-10 w-full shrink-0 border-t border-gray-200 bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
                    {replyingTo && (
                        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border-l-4 border-blue-500 bg-blue-50 px-3 py-2">
                            <div className="min-w-0">
                                <p className="text-xs font-semibold text-blue-700">
                                    Đang trả lời {replyingTo.username}
                                </p>
                                <p className="truncate text-sm text-gray-600">{replyingTo.content}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setReplyingTo(null)}
                                className="shrink-0 rounded px-2 py-1 text-gray-500 hover:bg-blue-100"
                                aria-label="Hủy trả lời"
                            >
                                ×
                            </button>
                        </div>
                    )}
                    {messageActionError && (
                        <p role="alert" className="mb-2 text-sm text-red-600">{messageActionError}</p>
                    )}
                    <div className="flex min-w-0 w-full gap-2">
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    const reader = new FileReader();
                                    reader.onload = (event) => {
                                        addMessage({
                                            id: `msg-${Date.now()}`,
                                            userId,
                                            username,
                                            content: `📷 Đã tải ảnh lên`,
                                            type: 'image',
                                            imageUrl: event.target?.result as string,
                                            timestamp: new Date(),
                                            isAdmin: true,
                                        });
                                    };
                                    reader.readAsDataURL(file);
                                }
                                e.target.value = '';
                            }}
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="shrink-0 rounded-lg p-2 hover:bg-gray-100"
                            title="Đính kèm hình ảnh"
                        >
                            🖼️
                        </button>
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="shrink-0 rounded-lg p-2 hover:bg-gray-100"
                            title="Đính kèm tệp"
                        >
                            📎
                        </button>
                        <input
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                            placeholder="Nhập tin nhắn..."
                            className="min-w-0 flex-1 rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            onClick={handleSendMessage}
                            className="shrink-0 rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                        >
                            📤
                        </button>
                    </div>
                </div>
                </>}
            </div>

            {isChangePasswordOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !isChangingPassword && setIsChangePasswordOpen(false)}>
                    <form
                        onSubmit={handleChangePassword}
                        onClick={(event) => event.stopPropagation()}
                        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Đổi mật khẩu</h2>
                                <p className="mt-1 text-sm text-gray-500">Nhập mật khẩu hiện tại và mật khẩu mới.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsChangePasswordOpen(false)}
                                disabled={isChangingPassword}
                                className="text-xl text-gray-500 disabled:opacity-50"
                                aria-label="Đóng"
                            >
                                ×
                            </button>
                        </div>
                        <div className="mt-5 space-y-3">
                            <input
                                type="password"
                                value={passwordForm.currentPassword}
                                onChange={(event) => setPasswordForm({ ...passwordForm, currentPassword: event.target.value })}
                                placeholder="Mật khẩu hiện tại"
                                required
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                            />
                            <input
                                type="password"
                                value={passwordForm.newPassword}
                                onChange={(event) => setPasswordForm({ ...passwordForm, newPassword: event.target.value })}
                                placeholder="Mật khẩu mới"
                                minLength={6}
                                required
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                            />
                            <input
                                type="password"
                                value={passwordForm.confirmPassword}
                                onChange={(event) => setPasswordForm({ ...passwordForm, confirmPassword: event.target.value })}
                                placeholder="Nhập lại mật khẩu mới"
                                minLength={6}
                                required
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                            />
                        </div>
                        {(passwordError || passwordMessage) && (
                            <p className={`mt-3 text-sm ${passwordError ? 'text-red-600' : 'text-green-600'}`}>
                                {passwordError || passwordMessage}
                            </p>
                        )}
                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setIsChangePasswordOpen(false)}
                                disabled={isChangingPassword}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Hủy
                            </button>
                            <button
                                type="submit"
                                disabled={isChangingPassword}
                                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isChangingPassword ? 'Đang lưu...' : 'Đổi mật khẩu'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default ChatInterface;
